/**
 * FILE TỰ SINH -- KHÔNG SỬA TAY.
 * Nguồn: prisma/schema.prisma | Công cụ: scripts/generate-zod-from-prisma.ts
 * Sinh lại bằng: pnpm db:zod
 *
 * - <Model>Schema            : hình dạng một dòng trong DB (mọi cột vô hướng)
 * - <Model>CreateInputSchema : dữ liệu client được phép gửi khi tạo mới
 *                              (bỏ id tự sinh, @updatedAt và các cột hệ thống;
 *                               cột nullable hoặc có @default trở thành optional)
 * - <X>EnumSchema            : enum của Prisma
 *
 * Rule nghiệp vụ (min/max, regex, message tiếng Việt) KHÔNG nằm ở đây -- thêm bằng
 * .extend() / .refine() ở tầng backend (packages/trpc) và giao diện (apps/web).
 */

import { z } from 'zod';

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(jsonValueSchema), z.record(jsonValueSchema)]),
);

// ── Enum ──

export const UserStatusEnumSchema = /* @__PURE__ */ z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED']);
export const NotificationTypeEnumSchema = /* @__PURE__ */ z.enum(['INFO', 'WARNING', 'ERROR', 'SUCCESS', 'SYSTEM']);
export const MaterialCategoryEnumSchema = /* @__PURE__ */ z.enum(['PAPER', 'INK', 'GLUE', 'FILM', 'CHEMICAL', 'OTHER']);
export const QuotationDetailPricingStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'ESTIMATING', 'ESTIMATED', 'APPROVED']);
export const QuotationStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'SENT', 'PROCESSING', 'ESTIMATED', 'APPROVED', 'REJECTED']);
export const OrderCategoryEnumSchema = /* @__PURE__ */ z.enum(['MANUFACTURING', 'OUTSOURCE', 'COMMERCIAL']);
export const OrderStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING', 'WAITING_PRE_PRESS', 'CONFIRMED', 'APPROVED', 'PLANNED', 'IN_PROGRESS', 'IN_PRODUCTION', 'COMPLETED', 'DELIVERED', 'INVOICED', 'CLOSED', 'CANCELLED']);
export const MoldStatusEnumSchema = /* @__PURE__ */ z.enum(['NEW', 'REUSE', 'REUSE_EDITED']);
export const ProductionPriorityEnumSchema = /* @__PURE__ */ z.enum(['LOW', 'NORMAL', 'HIGH', 'URGENT']);
export const PrepressStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'LAYOUT_PROCESSING', 'PLATE_OUTPUT', 'MOLD_ORDERED', 'COMPLETED']);
export const ProductionOrderStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING_APPROVAL', 'PRE_PRESS', 'READY_FOR_SCHEDULING', 'PRE_PRESS_COMPLETED', 'MATERIALS_READY', 'PLANNED', 'RELEASED', 'IN_PROGRESS', 'MANUFACTURED', 'COMPLETED', 'CLOSED', 'CANCELLED', 'ON_HOLD', 'PAUSED']);
export const ProductionOrderTypeEnumSchema = /* @__PURE__ */ z.enum(['SINGLE', 'GANG_RUN', 'SPLIT_BATCH', 'SPLIT_PART']);
export const DeploymentModelEnumSchema = /* @__PURE__ */ z.enum(['INTERNAL_100', 'OUTSOURCE_100', 'HYBRID_PRINT_INTERNAL', 'HYBRID_PRINT_EXTERNAL', 'HYBRID_FINISH_EXTERNAL', 'TRANSPORT']);
export const RoutingStepStatusEnumSchema = /* @__PURE__ */ z.enum(['PLANNED', 'READY', 'SCHEDULED', 'RUNNING', 'COMPLETED', 'PAUSED', 'CANCELLED']);
export const MaterialPlanningStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'ORDERED', 'PARTIAL_RECEIVED', 'RECEIVED', 'CANCELLED']);
export const MachineTypeEnumSchema = /* @__PURE__ */ z.enum(['OFFSET_PRINTER', 'FLEXO_PRINTER', 'PAPER_CUTTER', 'COATER', 'SILK_SCREEN', 'LAMINATOR', 'HOT_FOIL', 'DIE_CUTTER', 'WINDOW_PATCHER', 'FOLDER_GLUER', 'PACKING', 'OTHER']);
export const MachineStatusEnumSchema = /* @__PURE__ */ z.enum(['IDLE', 'RUNNING', 'MAINTENANCE', 'DOWN']);
export const WasteUnitEnumSchema = /* @__PURE__ */ z.enum(['SHEET', 'PCS']);
export const ToolingTypeEnumSchema = /* @__PURE__ */ z.enum(['PLATE', 'DIE_CUT', 'FOIL_STAMP', 'EMBOSS_DIE', 'DEBOSS_DIE', 'SPOT_UV', 'SILK_SCREEN']);
export const PriceUnitEnumSchema = /* @__PURE__ */ z.enum(['PER_SHEET', 'PER_M2', 'PER_ITEM']);
export const MachineLogTypeEnumSchema = /* @__PURE__ */ z.enum(['OPERATION', 'DOWNTIME', 'MAINTENANCE', 'SETUP']);
export const QCStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'INSPECTING', 'PASSED', 'WARNING', 'FAILED', 'REWORK', 'RE_INSPECT']);
export const NCRSourceEnumSchema = /* @__PURE__ */ z.enum(['INTERNAL', 'CUSTOMER', 'SUPPLIER']);
export const NCRSeverityEnumSchema = /* @__PURE__ */ z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const NCRStatusEnumSchema = /* @__PURE__ */ z.enum(['OPEN', 'INVESTIGATING', 'CORRECTIVE_ACTION', 'VERIFICATION', 'PREVENTIVE_ACTION', 'CLOSED', 'CANCELLED']);
export const OutsourcePOStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED']);
export const PaymentStatusEnumSchema = /* @__PURE__ */ z.enum(['UNPAID', 'PARTIAL', 'PAID']);
export const InventoryStatusEnumSchema = /* @__PURE__ */ z.enum(['AVAILABLE', 'RESERVED', 'LOW_STOCK', 'OUT_OF_STOCK']);
export const DocumentStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING_APPROVAL', 'COMPLETED', 'CANCELLED']);
export const DocumentTypeEnumSchema = /* @__PURE__ */ z.enum(['RECEIPT', 'ISSUE', 'ADJUSTMENT', 'TRANSFER']);
export const TransactionTypeEnumSchema = /* @__PURE__ */ z.enum(['RECEIPT', 'ISSUE', 'RESERVATION', 'TRANSFER', 'ADJUSTMENT']);
export const PurchaseOrderStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING', 'APPROVED', 'ORDERED', 'PARTIAL_RECEIVED', 'RECEIVED', 'CANCELLED']);
export const PurchaseRequestStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'ORDERED']);
export const DeliveryStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'SCHEDULED', 'IN_TRANSIT', 'DELIVERED', 'COMPLETED', 'CANCELLED']);
export const SpecStatusEnumSchema = /* @__PURE__ */ z.enum(['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'OBSOLETE']);
export const DesignStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'DESIGNING', 'REVIEWING', 'APPROVED', 'REJECTED']);
export const ToolingStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'AVAILABLE', 'IN_USE', 'MAINTENANCE', 'DAMAGED', 'DISCARDED', 'CANCELLED']);
export const FeedbackStatusEnumSchema = /* @__PURE__ */ z.enum(['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']);
export const FeedbackTypeEnumSchema = /* @__PURE__ */ z.enum(['BUG', 'IMPROVEMENT', 'QUESTION']);
export const FeedbackPriorityEnumSchema = /* @__PURE__ */ z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']);
export const KpiStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'REVIEWED', 'APPROVED', 'REJECTED']);
export const ReadinessStatusEnumSchema = /* @__PURE__ */ z.enum(['PENDING', 'PARTIAL', 'READY']);
export const DesignTypeEnumSchema = /* @__PURE__ */ z.enum(['NEW', 'EDIT']);
export const WarehouseTypeEnumSchema = /* @__PURE__ */ z.enum(['MATERIAL', 'FG', 'TOOLING', 'TECH']);
export const DebtTypeEnumSchema = /* @__PURE__ */ z.enum(['INCREASE', 'DECREASE']);

// ── Model ──

export const UserSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  employeeCode: z.string(),
  fullName: z.string(),
  department: z.string(),
  position: z.string(),
  section: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string(),
  password: z.string(),
  resetOtp: z.string().nullable(),
  resetOtpExpiresAt: z.coerce.date().nullable(),
  avatar: z.string().nullable(),
  signature: z.string().nullable(),
  role: z.string(),
  status: UserStatusEnumSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  gender: z.string().nullable(),
  dateOfBirth: z.coerce.date().nullable(),
  placeOfBirth: z.string().nullable(),
  idCardNumber: z.string().nullable(),
  idCardIssueDate: z.coerce.date().nullable(),
  idCardIssuePlace: z.string().nullable(),
  address: z.string().nullable(),
  currentAddress: z.string().nullable(),
  maritalStatus: z.string().nullable(),
  emergencyContact: z.string().nullable(),
  criminalRecord: z.string().nullable(),
  educationLevel: z.string().nullable(),
  joinDate: z.coerce.date().nullable(),
  officialDate: z.coerce.date().nullable(),
  bankName: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankBranch: z.string().nullable(),
  taxNumber: z.string().nullable(),
  socialInsuranceNumber: z.string().nullable(),
  allowances: jsonValueSchema.nullable(),
});
export const UserCreateInputSchema = /* @__PURE__ */ UserSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ section: true, phone: true, resetOtp: true, resetOtpExpiresAt: true, avatar: true, signature: true, status: true, gender: true, dateOfBirth: true, placeOfBirth: true, idCardNumber: true, idCardIssueDate: true, idCardIssuePlace: true, address: true, currentAddress: true, maritalStatus: true, emergencyContact: true, criminalRecord: true, educationLevel: true, joinDate: true, officialDate: true, bankName: true, bankAccountNumber: true, bankBranch: true, taxNumber: true, socialInsuranceNumber: true, allowances: true });
export type UserRow = z.infer<typeof UserSchema>;
export type UserCreateInput = z.infer<typeof UserCreateInputSchema>;

export const EmployeeContractSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  contractNumber: z.string(),
  contractType: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().nullable(),
  basicSalary: z.number().nullable(),
  fileUrl: z.string().nullable(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const EmployeeContractCreateInputSchema = /* @__PURE__ */ EmployeeContractSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ endDate: true, basicSalary: true, fileUrl: true });
export type EmployeeContractRow = z.infer<typeof EmployeeContractSchema>;
export type EmployeeContractCreateInput = z.infer<typeof EmployeeContractCreateInputSchema>;

export const SessionSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  accessToken: z.string(),
  refreshToken: z.string(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const SessionCreateInputSchema = /* @__PURE__ */ SessionSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ userAgent: true, ipAddress: true });
export type SessionRow = z.infer<typeof SessionSchema>;
export type SessionCreateInput = z.infer<typeof SessionCreateInputSchema>;

export const RoleSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  roleId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: jsonValueSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const RoleCreateInputSchema = /* @__PURE__ */ RoleSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ description: true, permissions: true });
export type RoleRow = z.infer<typeof RoleSchema>;
export type RoleCreateInput = z.infer<typeof RoleCreateInputSchema>;

export const DepartmentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  name: z.string(),
  code: z.string(),
  description: z.string().nullable(),
  headUserId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const DepartmentCreateInputSchema = /* @__PURE__ */ DepartmentSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ description: true, headUserId: true });
export type DepartmentRow = z.infer<typeof DepartmentSchema>;
export type DepartmentCreateInput = z.infer<typeof DepartmentCreateInputSchema>;

export const SystemConfigSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  key: z.string(),
  value: z.string(),
  type: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const SystemConfigCreateInputSchema = /* @__PURE__ */ SystemConfigSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true });
export type SystemConfigRow = z.infer<typeof SystemConfigSchema>;
export type SystemConfigCreateInput = z.infer<typeof SystemConfigCreateInputSchema>;

export const NotificationSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  type: NotificationTypeEnumSchema,
  title: z.string(),
  message: z.string(),
  isRead: z.boolean(),
  link: z.string().nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const NotificationCreateInputSchema = /* @__PURE__ */ NotificationSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ isRead: true, link: true });
export type NotificationRow = z.infer<typeof NotificationSchema>;
export type NotificationCreateInput = z.infer<typeof NotificationCreateInputSchema>;

export const SystemChangelogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  version: z.string(),
  date: z.coerce.date(),
  title: z.string(),
  changes: jsonValueSchema,
  relatedModules: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const SystemChangelogCreateInputSchema = /* @__PURE__ */ SystemChangelogSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ relatedModules: true });
export type SystemChangelogRow = z.infer<typeof SystemChangelogSchema>;
export type SystemChangelogCreateInput = z.infer<typeof SystemChangelogCreateInputSchema>;

export const SequenceSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  entity: z.string(),
  prefix: z.string(),
  yearMonth: z.string(),
  lastSeq: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const SequenceCreateInputSchema = /* @__PURE__ */ SequenceSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ lastSeq: true });
export type SequenceRow = z.infer<typeof SequenceSchema>;
export type SequenceCreateInput = z.infer<typeof SequenceCreateInputSchema>;

export const TrashRecordSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  module: z.string(),
  recordId: z.string(),
  originalData: jsonValueSchema,
  deletedBy: z.string().nullable(),
  deletedAt: z.coerce.date(),
  batchId: z.string().nullable(),
});
export const TrashRecordCreateInputSchema = /* @__PURE__ */ TrashRecordSchema.omit({ id: true, deletedBy: true, deletedAt: true }).partial({ batchId: true });
export type TrashRecordRow = z.infer<typeof TrashRecordSchema>;
export type TrashRecordCreateInput = z.infer<typeof TrashRecordCreateInputSchema>;

export const CustomerSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  type: z.string().nullable(),
  group: z.string().nullable(),
  shortName: z.string().nullable(),
  name: z.string(),
  address: z.string().nullable(),
  taxCode: z.string().nullable(),
  representative: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  fanpage: z.string().nullable(),
  logo: z.string().nullable(),
  panel: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankAccountHolder: z.string().nullable(),
  bankName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  deliveryMethod: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  transitAddress: z.string().nullable(),
  initialDebt: z.number(),
  currentDebt: z.number(),
  openingDebt: z.number(),
  orderValue: z.number(),
  deliveryValue: z.number(),
  paidAmount: z.number(),
  remainingDebt: z.number(),
  paymentTerms: z.string().nullable(),
  creditLimit: z.number().nullable(),
  notes: z.string().nullable(),
  tags: z.string().nullable(),
  salesUserId: z.string().nullable(),
  position: z.string().nullable(),
  colorTolerance: z.string().nullable(),
  quantityTolerance: z.string().nullable(),
  tolerancePercent: z.number(),
  status: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CustomerCreateInputSchema = /* @__PURE__ */ CustomerSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ type: true, group: true, shortName: true, address: true, taxCode: true, representative: true, phone: true, website: true, fanpage: true, logo: true, panel: true, bankAccountNumber: true, bankAccountHolder: true, bankName: true, contactPerson: true, mobile: true, email: true, deliveryMethod: true, deliveryAddress: true, transitAddress: true, initialDebt: true, currentDebt: true, openingDebt: true, orderValue: true, deliveryValue: true, paidAmount: true, remainingDebt: true, paymentTerms: true, creditLimit: true, notes: true, tags: true, salesUserId: true, position: true, colorTolerance: true, quantityTolerance: true, tolerancePercent: true, status: true });
export type CustomerRow = z.infer<typeof CustomerSchema>;
export type CustomerCreateInput = z.infer<typeof CustomerCreateInputSchema>;

export const CustomerSalesUserSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  customerId: z.string(),
  userId: z.string(),
  department: z.string().nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CustomerSalesUserCreateInputSchema = /* @__PURE__ */ CustomerSalesUserSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ department: true });
export type CustomerSalesUserRow = z.infer<typeof CustomerSalesUserSchema>;
export type CustomerSalesUserCreateInput = z.infer<typeof CustomerSalesUserCreateInputSchema>;

export const MaterialSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  materialId: z.string(),
  name: z.string(),
  typeId: z.string().nullable(),
  vendorId: z.string().nullable(),
  category: MaterialCategoryEnumSchema.nullable(),
  subType: z.string().nullable(),
  specs: z.string().nullable(),
  gsm: z.string().nullable(),
  width: z.number().nullable(),
  length: z.number().nullable(),
  unit: z.string().nullable(),
  initialStock: z.number().nullable(),
  currentStock: z.number().nullable(),
  minStock: z.number().int().nullable(),
  unitPrice: z.number().nullable(),
  image: z.string().nullable(),
  note: z.string().nullable(),
  pricingModel: z.string().nullable(),
  pricePerTon: z.number().nullable(),
  pricePerM2: z.number().nullable(),
  pricePerReam: z.number().nullable(),
  sheetsPerReam: z.number().int().nullable(),
  brand: z.string().nullable(),
  height: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MaterialCreateInputSchema = /* @__PURE__ */ MaterialSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ typeId: true, vendorId: true, category: true, subType: true, specs: true, gsm: true, width: true, length: true, unit: true, initialStock: true, currentStock: true, minStock: true, unitPrice: true, image: true, note: true, pricingModel: true, pricePerTon: true, pricePerM2: true, pricePerReam: true, sheetsPerReam: true, brand: true, height: true });
export type MaterialRow = z.infer<typeof MaterialSchema>;
export type MaterialCreateInput = z.infer<typeof MaterialCreateInputSchema>;

export const MaterialTypeSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  group: z.string().nullable(),
  material: z.string().nullable(),
  gsm: z.string().nullable(),
  label: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MaterialTypeCreateInputSchema = /* @__PURE__ */ MaterialTypeSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ group: true, material: true, gsm: true, label: true });
export type MaterialTypeRow = z.infer<typeof MaterialTypeSchema>;
export type MaterialTypeCreateInput = z.infer<typeof MaterialTypeCreateInputSchema>;

export const QuotationSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  quotationId: z.string(),
  version: z.number().int(),
  customerId: z.string(),
  createdDate: z.coerce.date(),
  validUntil: z.coerce.date(),
  approvedDate: z.coerce.date().nullable(),
  status: QuotationStatusEnumSchema,
  progress: z.string().nullable(),
  vatRate: z.number().nullable(),
  exchangeRate: z.number().nullable(),
  shippingFee: z.number().nullable(),
  deposit: z.string().nullable(),
  paymentTerm: z.string().nullable(),
  margin: z.number().nullable(),
  quantityTolerance: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  contactPerson: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  deliveryTime: z.string().nullable(),
  creatorId: z.string(),
  approverId: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isNonMoq: z.boolean(),
  isOutsourced: z.boolean(),
  supplierCost: z.number().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QuotationCreateInputSchema = /* @__PURE__ */ QuotationSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ version: true, approvedDate: true, progress: true, vatRate: true, exchangeRate: true, shippingFee: true, deposit: true, paymentTerm: true, margin: true, quantityTolerance: true, deliveryAddress: true, contactPerson: true, phone: true, email: true, deliveryTime: true, approverId: true, notes: true, isNonMoq: true, isOutsourced: true, supplierCost: true });
export type QuotationRow = z.infer<typeof QuotationSchema>;
export type QuotationCreateInput = z.infer<typeof QuotationCreateInputSchema>;

export const QuotationDetailSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  quotationId: z.string(),
  productCode: z.string().nullable(),
  productId: z.string().nullable(),
  productName: z.string(),
  productGroup: z.string().nullable(),
  image: z.string().nullable(),
  process: z.string().nullable(),
  materialId: z.string().nullable(),
  specs: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number().int(),
  priceVND: z.number(),
  priceUSD: z.number().nullable(),
  purchasePrice: z.number().nullable(),
  costPrice: z.number().nullable(),
  profitMargin: z.number().nullable(),
  status: z.string().nullable(),
  pricingStatus: QuotationDetailPricingStatusEnumSchema,
  notes: z.string().nullable(),
  tags: z.string().nullable(),
  isCombo: z.boolean(),
  productionPrice: z.number().nullable(),
  quotedPrice: z.number().nullable(),
  isCommercial: z.boolean(),
  isTechnicalCalculated: z.boolean(),
  costEstimateId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isComposite: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QuotationDetailCreateInputSchema = /* @__PURE__ */ QuotationDetailSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productCode: true, productId: true, productGroup: true, image: true, process: true, materialId: true, specs: true, unit: true, priceUSD: true, purchasePrice: true, costPrice: true, profitMargin: true, status: true, pricingStatus: true, notes: true, tags: true, isCombo: true, productionPrice: true, quotedPrice: true, isCommercial: true, isTechnicalCalculated: true, costEstimateId: true, isComposite: true });
export type QuotationDetailRow = z.infer<typeof QuotationDetailSchema>;
export type QuotationDetailCreateInput = z.infer<typeof QuotationDetailCreateInputSchema>;

export const QuotationDetailComponentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  quotationDetailId: z.string(),
  componentName: z.string(),
  quantity: z.number(),
  unit: z.string().nullable(),
  materialId: z.string().nullable(),
  process: z.string().nullable(),
  specs: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QuotationDetailComponentCreateInputSchema = /* @__PURE__ */ QuotationDetailComponentSchema.omit({ id: true, isDeleted: true, deletedAt: true }).partial({ quantity: true, unit: true, materialId: true, process: true, specs: true });
export type QuotationDetailComponentRow = z.infer<typeof QuotationDetailComponentSchema>;
export type QuotationDetailComponentCreateInput = z.infer<typeof QuotationDetailComponentCreateInputSchema>;

export const OrderSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  orderId: z.string(),
  customerId: z.string(),
  contactPerson: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  tolerancePercent: z.number(),
  completionPercent: z.number(),
  vatRate: z.number(),
  vatAmount: z.number(),
  shippingFee: z.number(),
  totalAmount: z.number(),
  currency: z.string(),
  exchangeRate: z.number(),
  deposit: z.string().nullable(),
  paymentTerm: z.string().nullable(),
  orderDate: z.coerce.date(),
  deliveryDeadline: z.coerce.date().nullable(),
  salesUserId: z.string().nullable(),
  quotationId: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  deliveryMethod: z.string().nullable(),
  deliveryNotes: z.string().nullable(),
  status: OrderStatusEnumSchema,
  priority: ProductionPriorityEnumSchema,
  paymentStatus: PaymentStatusEnumSchema,
  orderType: z.string(),
  category: OrderCategoryEnumSchema,
  sampleType: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  version: z.number().int(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
});
export const OrderCreateInputSchema = /* @__PURE__ */ OrderSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true, createdBy: true }).partial({ contactPerson: true, phone: true, email: true, tolerancePercent: true, completionPercent: true, vatRate: true, vatAmount: true, shippingFee: true, totalAmount: true, currency: true, exchangeRate: true, deposit: true, paymentTerm: true, deliveryDeadline: true, salesUserId: true, quotationId: true, deliveryAddress: true, deliveryMethod: true, deliveryNotes: true, priority: true, paymentStatus: true, orderType: true, category: true, sampleType: true, notes: true, version: true });
export type OrderRow = z.infer<typeof OrderSchema>;
export type OrderCreateInput = z.infer<typeof OrderCreateInputSchema>;

export const OrderDetailSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  detailId: z.string(),
  orderId: z.string(),
  productCode: z.string().nullable(),
  productId: z.string().nullable(),
  productName: z.string(),
  poReference: z.string().max(64).nullable(),
  soReference: z.string().max(64).nullable(),
  customerPo: z.string().nullable(),
  pnNo: z.string().nullable(),
  pjNo: z.string().nullable(),
  stockType: z.string().nullable(),
  quantity: z.number().int(),
  producedQty: z.number().int(),
  completedPercent: z.number(),
  stockQty: z.number().int().nullable(),
  unit: z.string().nullable(),
  specs: z.string().nullable(),
  process: z.string().nullable(),
  materialId: z.string().nullable(),
  purchasePrice: z.number().nullable(),
  unitPrice: z.number(),
  amount: z.number(),
  priceVND: z.number().nullable(),
  priceUSD: z.number().nullable(),
  totalVND: z.number().nullable(),
  totalUSD: z.number().nullable(),
  deliveryDate: z.coerce.date().nullable(),
  deliveryDeadline: z.coerce.date().nullable(),
  qtyDelivered: z.number().int(),
  plannedQty: z.number().int(),
  remainingPlanQty: z.number().int(),
  version: z.number().int(),
  lsxId: z.string().nullable(),
  lsxQty: z.number().int().nullable(),
  quotationDetailId: z.string().nullable(),
  printLayoutId: z.string().nullable(),
  allowancePercent: z.number().nullable(),
  completedQty: z.number().int(),
  status: z.string(),
  prepressCode: z.string().nullable(),
  prepressPaperSize: z.string().nullable(),
  prepressUps: z.number().int().nullable(),
  prepressPrintSheets: z.number().int().nullable(),
  prepressNotes: z.string().nullable(),
  prepressCount: z.number().int().nullable(),
  imposedQty: z.number().int(),
  isPrepressCompleted: z.boolean(),
  rePrepressReason: z.string().nullable(),
  rePrepressAt: z.coerce.date().nullable(),
  rePrepressBy: z.string().max(128).nullable(),
  rePrepressCount: z.number().int(),
  overrunQty: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isComposite: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const OrderDetailCreateInputSchema = /* @__PURE__ */ OrderDetailSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productCode: true, productId: true, poReference: true, soReference: true, customerPo: true, pnNo: true, pjNo: true, stockType: true, producedQty: true, completedPercent: true, stockQty: true, unit: true, specs: true, process: true, materialId: true, purchasePrice: true, amount: true, priceVND: true, priceUSD: true, totalVND: true, totalUSD: true, deliveryDate: true, deliveryDeadline: true, qtyDelivered: true, plannedQty: true, remainingPlanQty: true, version: true, lsxId: true, lsxQty: true, quotationDetailId: true, printLayoutId: true, allowancePercent: true, completedQty: true, status: true, prepressCode: true, prepressPaperSize: true, prepressUps: true, prepressPrintSheets: true, prepressNotes: true, prepressCount: true, imposedQty: true, isPrepressCompleted: true, rePrepressReason: true, rePrepressAt: true, rePrepressBy: true, rePrepressCount: true, overrunQty: true, notes: true, isComposite: true });
export type OrderDetailRow = z.infer<typeof OrderDetailSchema>;
export type OrderDetailCreateInput = z.infer<typeof OrderDetailCreateInputSchema>;

export const OrderDetailComponentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  orderDetailId: z.string(),
  componentName: z.string(),
  quantity: z.number().int(),
  unit: z.string().nullable(),
  materialId: z.string().nullable(),
  process: z.string().nullable(),
  specs: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const OrderDetailComponentCreateInputSchema = /* @__PURE__ */ OrderDetailComponentSchema.omit({ id: true, isDeleted: true, deletedAt: true }).partial({ quantity: true, unit: true, materialId: true, process: true, specs: true });
export type OrderDetailComponentRow = z.infer<typeof OrderDetailComponentSchema>;
export type OrderDetailComponentCreateInput = z.infer<typeof OrderDetailComponentCreateInputSchema>;

export const ProductionOrderSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  orderId: z.string(),
  poReference: z.string().nullable(),
  salesOrderId: z.string().nullable(),
  priority: ProductionPriorityEnumSchema,
  productId: z.string().nullable(),
  techSpecId: z.string().nullable(),
  productName: z.string().nullable(),
  productLine: z.string().nullable(),
  customerId: z.string().nullable(),
  qtyOrder: z.number().int().nullable(),
  qtyAllowance: z.number().int().nullable(),
  qtyPlan: z.number().int().nullable(),
  actualQuantity: z.number().int().nullable(),
  dieCutId: z.string().nullable(),
  isNewMold: z.boolean(),
  moldStatus: z.string(),
  plateStatus: z.string(),
  toolingStatus: ReadinessStatusEnumSchema,
  materialStatus: ReadinessStatusEnumSchema,
  techSpecs: jsonValueSchema.nullable(),
  bomEstimator: jsonValueSchema.nullable(),
  deploymentModel: DeploymentModelEnumSchema.nullable(),
  status: ProductionOrderStatusEnumSchema,
  poType: ProductionOrderTypeEnumSchema,
  parentOrderId: z.string().nullable(),
  isPlanLocked: z.boolean(),
  isPlateExported: z.boolean(),
  isFileApproved: z.boolean(),
  prepressStatus: PrepressStatusEnumSchema,
  layoutCode: z.string().nullable(),
  ups: z.number().int().nullable(),
  netSheets: z.number().int().nullable(),
  runSheetsNeeded: z.number().int().nullable(),
  sheetSize: z.string().nullable(),
  prepressMinutes: jsonValueSchema.nullable(),
  deadline: z.coerce.date().nullable(),
  scheduleLateDays: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  orderDetailId: z.string().nullable(),
  isComposite: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
});
export const ProductionOrderCreateInputSchema = /* @__PURE__ */ ProductionOrderSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true, createdBy: true }).partial({ poReference: true, salesOrderId: true, priority: true, productId: true, techSpecId: true, productName: true, productLine: true, customerId: true, qtyOrder: true, qtyAllowance: true, qtyPlan: true, actualQuantity: true, dieCutId: true, isNewMold: true, moldStatus: true, plateStatus: true, toolingStatus: true, materialStatus: true, techSpecs: true, bomEstimator: true, deploymentModel: true, status: true, poType: true, parentOrderId: true, isPlanLocked: true, isPlateExported: true, isFileApproved: true, prepressStatus: true, layoutCode: true, ups: true, netSheets: true, runSheetsNeeded: true, sheetSize: true, prepressMinutes: true, deadline: true, scheduleLateDays: true, orderDetailId: true, isComposite: true });
export type ProductionOrderRow = z.infer<typeof ProductionOrderSchema>;
export type ProductionOrderCreateInput = z.infer<typeof ProductionOrderCreateInputSchema>;

export const ProductionOrderSourceSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  orderDetailId: z.string(),
  salesOrderId: z.string(),
  quantity: z.number(),
  pcsPerSheet: z.number().int(),
  inventoryQty: z.number().int(),
  plannedQty: z.number().int(),
  finishedQty: z.number().int(),
  deliveredQty: z.number().int(),
  progressPercent: z.number(),
  deliveryNotes: z.string().nullable(),
  finishingNotes: z.string().nullable(),
});
export const ProductionOrderSourceCreateInputSchema = /* @__PURE__ */ ProductionOrderSourceSchema.omit({ id: true }).partial({ pcsPerSheet: true, inventoryQty: true, plannedQty: true, finishedQty: true, deliveredQty: true, progressPercent: true, deliveryNotes: true, finishingNotes: true });
export type ProductionOrderSourceRow = z.infer<typeof ProductionOrderSourceSchema>;
export type ProductionOrderSourceCreateInput = z.infer<typeof ProductionOrderSourceCreateInputSchema>;

export const ProductionOrderPrepressSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  prepressPcsPerSheet: z.number().int().nullable(),
  prepressWasteAllowance: z.number().int().nullable(),
  prepressWastePercentage: z.number().nullable(),
  totalTargetQty: z.number().int().nullable(),
  totalPlanSheets: z.number().int().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProductionOrderPrepressCreateInputSchema = /* @__PURE__ */ ProductionOrderPrepressSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ prepressPcsPerSheet: true, prepressWasteAllowance: true, prepressWastePercentage: true, totalTargetQty: true, totalPlanSheets: true });
export type ProductionOrderPrepressRow = z.infer<typeof ProductionOrderPrepressSchema>;
export type ProductionOrderPrepressCreateInput = z.infer<typeof ProductionOrderPrepressCreateInputSchema>;

export const ProductionOrderToolingSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  toolingType: z.string(),
  toolingId: z.string().nullable(),
  quantity: z.number(),
  notes: z.string().nullable(),
  status: MoldStatusEnumSchema.nullable(),
  readinessStatus: ReadinessStatusEnumSchema,
  expectedDate: z.coerce.date().nullable(),
  dieCutId: z.string().nullable(),
  processCode: z.string().nullable(),
  processName: z.string().nullable(),
  moldCode: z.string().nullable(),
  dimensions: z.string().nullable(),
  location: z.string().nullable(),
  outsourcePoId: z.string().nullable(),
  dieCutStyle: z.string().nullable(),
  areaCm2: z.number().nullable(),
  plateColor: z.string().nullable(),
  plateSize: z.string().nullable(),
  machineId: z.string().nullable(),
  plateSource: z.string().nullable(),
  componentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProductionOrderToolingCreateInputSchema = /* @__PURE__ */ ProductionOrderToolingSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ toolingId: true, quantity: true, notes: true, status: true, readinessStatus: true, expectedDate: true, dieCutId: true, processCode: true, processName: true, moldCode: true, dimensions: true, location: true, outsourcePoId: true, dieCutStyle: true, areaCm2: true, plateColor: true, plateSize: true, machineId: true, plateSource: true, componentId: true });
export type ProductionOrderToolingRow = z.infer<typeof ProductionOrderToolingSchema>;
export type ProductionOrderToolingCreateInput = z.infer<typeof ProductionOrderToolingCreateInputSchema>;

export const ProductionOrderMaterialSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  purchasedPaperSize: z.string().nullable(),
  printingPaperSize: z.string().nullable(),
  cutRatio: z.number().int().nullable(),
  totalMaterialRequired: z.number().int().nullable(),
  specification: z.string().nullable(),
  standardQuantity: z.number().int().nullable(),
  allowance: z.number().int().nullable(),
  itemCode: z.string().nullable(),
  poNumber: z.string().nullable(),
  customerGroup: z.string().nullable(),
  issuedSheets: z.number().int(),
  bufferSheets: z.number().int(),
  stockPcs: z.number().int(),
  returnedSheets: z.number().int(),
  isSurplusReturned: z.boolean(),
  surplusReturnedAt: z.coerce.date().nullable(),
  surplusReturnNotes: z.string().nullable(),
  grainDirection: z.string().nullable(),
  isSlitted: z.boolean(),
  slittedSheets: z.number().int().nullable(),
  slittedAt: z.coerce.date().nullable(),
  slittedBy: z.string().nullable(),
  palletCount: z.number().int().nullable(),
  slittingMachineId: z.string().nullable(),
  issueDocNo: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  printLayoutId: z.string().nullable(),
});
export const ProductionOrderMaterialCreateInputSchema = /* @__PURE__ */ ProductionOrderMaterialSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ purchasedPaperSize: true, printingPaperSize: true, cutRatio: true, totalMaterialRequired: true, specification: true, standardQuantity: true, allowance: true, itemCode: true, poNumber: true, customerGroup: true, issuedSheets: true, bufferSheets: true, stockPcs: true, returnedSheets: true, isSurplusReturned: true, surplusReturnedAt: true, surplusReturnNotes: true, grainDirection: true, isSlitted: true, slittedSheets: true, slittedAt: true, slittedBy: true, palletCount: true, slittingMachineId: true, issueDocNo: true, printLayoutId: true });
export type ProductionOrderMaterialRow = z.infer<typeof ProductionOrderMaterialSchema>;
export type ProductionOrderMaterialCreateInput = z.infer<typeof ProductionOrderMaterialCreateInputSchema>;

export const ProductionOrderComponentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  componentName: z.string(),
  isMain: z.boolean(),
  techSpecs: jsonValueSchema.nullable(),
  deploymentModel: DeploymentModelEnumSchema.nullable(),
  outsourceId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  parentComponentId: z.string().nullable(),
  productionOrderSourceId: z.string().nullable(),
  productId: z.string().nullable(),
  sortOrder: z.number().int(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductionOrderComponentCreateInputSchema = /* @__PURE__ */ ProductionOrderComponentSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ isMain: true, techSpecs: true, deploymentModel: true, outsourceId: true, parentComponentId: true, productionOrderSourceId: true, productId: true, sortOrder: true });
export type ProductionOrderComponentRow = z.infer<typeof ProductionOrderComponentSchema>;
export type ProductionOrderComponentCreateInput = z.infer<typeof ProductionOrderComponentCreateInputSchema>;

export const RoutingStepSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  stepOrder: z.number().int(),
  stepId: z.string(),
  name: z.string(),
  resourceId: z.string().nullable(),
  toolId: z.string().nullable(),
  inputs: z.array(z.string()),
  standardQuantity: z.number().int().nullable(),
  actualQuantity: z.number().int().nullable(),
  technicalRequirements: z.string().nullable(),
  estDurationHours: z.number().nullable(),
  overlappingMinutes: z.number().int().nullable(),
  plannedStartDate: z.coerce.date().nullable(),
  plannedEndDate: z.coerce.date().nullable(),
  overlapPercent: z.number().nullable(),
  status: RoutingStepStatusEnumSchema,
  qcStatus: QCStatusEnumSchema.nullable(),
  machineId: z.string().nullable(),
  operatorId: z.string().nullable(),
  sequenceNumber: z.number().int(),
  scheduledStartTime: z.coerce.date().nullable(),
  scheduledEndTime: z.coerce.date().nullable(),
  scheduleLocked: z.boolean(),
  scheduleLockedBy: z.string().nullable(),
  scheduleLockedAt: z.coerce.date().nullable(),
  scheduleNote: z.string().nullable(),
  scheduleSuggestions: jsonValueSchema.nullable(),
  plannedSetupTime: z.number().int().nullable(),
  plannedSpeed: z.number().nullable(),
  plannedTotalTime: z.number().int().nullable(),
  plannedSetupWaste: z.number().int().nullable(),
  plannedRunWastePct: z.number().nullable(),
  isOutsourced: z.boolean(),
  actualSetupTime: z.number().int(),
  actualRunTime: z.number().int(),
  actualQtyOk: z.number().int(),
  actualQtyNg: z.number().int(),
  plannedInputQty: z.number().int().nullable(),
  actualInputQty: z.number().int().nullable(),
  inputSourceType: z.string().nullable(),
  productionUnit: z.string().nullable(),
  actuals: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  componentId: z.string().nullable(),
  processGroupId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const RoutingStepCreateInputSchema = /* @__PURE__ */ RoutingStepSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ resourceId: true, toolId: true, inputs: true, standardQuantity: true, actualQuantity: true, technicalRequirements: true, estDurationHours: true, overlappingMinutes: true, plannedStartDate: true, plannedEndDate: true, overlapPercent: true, status: true, qcStatus: true, machineId: true, operatorId: true, sequenceNumber: true, scheduledStartTime: true, scheduledEndTime: true, scheduleLocked: true, scheduleLockedBy: true, scheduleLockedAt: true, scheduleNote: true, scheduleSuggestions: true, plannedSetupTime: true, plannedSpeed: true, plannedTotalTime: true, plannedSetupWaste: true, plannedRunWastePct: true, isOutsourced: true, actualSetupTime: true, actualRunTime: true, actualQtyOk: true, actualQtyNg: true, plannedInputQty: true, actualInputQty: true, inputSourceType: true, productionUnit: true, actuals: true, componentId: true, processGroupId: true });
export type RoutingStepRow = z.infer<typeof RoutingStepSchema>;
export type RoutingStepCreateInput = z.infer<typeof RoutingStepCreateInputSchema>;

export const BOMSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  materialId: z.string(),
  specification: z.string().nullable(),
  qtyRequired: z.number(),
  qtyIssued: z.number(),
  wastagePercent: z.number(),
  routingStepId: z.string().nullable(),
  unit: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  componentId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const BOMCreateInputSchema = /* @__PURE__ */ BOMSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ specification: true, qtyIssued: true, wastagePercent: true, routingStepId: true, unit: true, notes: true, componentId: true });
export type BOMRow = z.infer<typeof BOMSchema>;
export type BOMCreateInput = z.infer<typeof BOMCreateInputSchema>;

export const ProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  stepId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  group: z.string().nullable(),
  defaultMachineType: z.string().nullable(),
  unit: z.string().nullable(),
  cmyk: z.boolean(),
  pantone: z.boolean(),
  requiresTooling: z.array(ToolingTypeEnumSchema),
  setupCost: z.number().nullable(),
  unitCost: z.number().nullable(),
  speed: z.number().int().nullable(),
  productionTime: z.number().int().nullable(),
  wastePercent: z.number().nullable(),
  minWasteQty: z.number().int().nullable(),
  wasteUnit: WasteUnitEnumSchema.nullable(),
  priceUnit: PriceUnitEnumSchema.nullable(),
  defaultSetupTime: z.number().int().nullable(),
  defaultSpeed: z.number().int().nullable(),
  defaultSetupWaste: z.number().int().nullable(),
  defaultRunWastePct: z.number().nullable(),
  defaultBatchCost: z.number().nullable(),
  defaultUnitCost: z.number().nullable(),
  specs: jsonValueSchema.nullable(),
  requiresQc: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  processGroupId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProcessCreateInputSchema = /* @__PURE__ */ ProcessSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ description: true, group: true, defaultMachineType: true, unit: true, cmyk: true, pantone: true, requiresTooling: true, setupCost: true, unitCost: true, speed: true, productionTime: true, wastePercent: true, minWasteQty: true, wasteUnit: true, priceUnit: true, defaultSetupTime: true, defaultSpeed: true, defaultSetupWaste: true, defaultRunWastePct: true, defaultBatchCost: true, defaultUnitCost: true, specs: true, requiresQc: true, sortOrder: true, processGroupId: true });
export type ProcessRow = z.infer<typeof ProcessSchema>;
export type ProcessCreateInput = z.infer<typeof ProcessCreateInputSchema>;

export const ProcessGroupSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  color: z.string().nullable(),
  description: z.string().nullable(),
  isSystem: z.boolean(),
  planningStage: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProcessGroupCreateInputSchema = /* @__PURE__ */ ProcessGroupSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ sortOrder: true, isActive: true, color: true, description: true, isSystem: true, planningStage: true });
export type ProcessGroupRow = z.infer<typeof ProcessGroupSchema>;
export type ProcessGroupCreateInput = z.infer<typeof ProcessGroupCreateInputSchema>;

export const ProcessWasteMatrixSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  processId: z.string(),
  materialGroup: z.string().nullable(),
  moqFrom: z.number().int(),
  moqTo: z.number().int().nullable(),
  wastePercent: z.number(),
  wasteFixed: z.number().int(),
  notes: z.string().nullable(),
  source: z.string().nullable(),
  isActive: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProcessWasteMatrixCreateInputSchema = /* @__PURE__ */ ProcessWasteMatrixSchema.omit({ id: true, isDeleted: true, deletedAt: true, createdAt: true, updatedAt: true }).partial({ materialGroup: true, moqTo: true, wasteFixed: true, notes: true, source: true, isActive: true });
export type ProcessWasteMatrixRow = z.infer<typeof ProcessWasteMatrixSchema>;
export type ProcessWasteMatrixCreateInput = z.infer<typeof ProcessWasteMatrixCreateInputSchema>;

export const UserProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  processId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const UserProcessCreateInputSchema = /* @__PURE__ */ UserProcessSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type UserProcessRow = z.infer<typeof UserProcessSchema>;
export type UserProcessCreateInput = z.infer<typeof UserProcessCreateInputSchema>;

export const MachineProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  machineId: z.string(),
  processId: z.string(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MachineProcessCreateInputSchema = /* @__PURE__ */ MachineProcessSchema.omit({ id: true, isDeleted: true, deletedAt: true });
export type MachineProcessRow = z.infer<typeof MachineProcessSchema>;
export type MachineProcessCreateInput = z.infer<typeof MachineProcessCreateInputSchema>;

export const MachineLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  logId: z.string(),
  machineId: z.string(),
  productionOrderId: z.string().nullable(),
  date: z.coerce.date(),
  shift: z.string().nullable(),
  type: MachineLogTypeEnumSchema,
  status: z.string().nullable(),
  durationMinutes: z.number().int().nullable(),
  parameters: jsonValueSchema.nullable(),
  issueType: z.string().nullable(),
  reason: z.string().nullable(),
  fixAction: z.string().nullable(),
  operatorId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MachineLogCreateInputSchema = /* @__PURE__ */ MachineLogSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productionOrderId: true, shift: true, status: true, durationMinutes: true, parameters: true, issueType: true, reason: true, fixAction: true, operatorId: true });
export type MachineLogRow = z.infer<typeof MachineLogSchema>;
export type MachineLogCreateInput = z.infer<typeof MachineLogCreateInputSchema>;

export const ProductionLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  logId: z.string(),
  productionOrderId: z.string(),
  machineId: z.string().nullable(),
  stepId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  date: z.coerce.date(),
  shift: z.string().nullable(),
  qtyOk: z.number().int(),
  qtyNg: z.number().int(),
  unitCost: z.number().nullable(),
  setupCost: z.number().nullable(),
  pieceRateWage: z.number().nullable(),
  setupMinutes: z.number().int(),
  breakMinutes: z.number().int(),
  waitMinutes: z.string().nullable(),
  stopLossMinutes: z.string().nullable(),
  operatorTeam: z.string().nullable(),
  printSize: z.string().nullable(),
  toolingCode: z.string().nullable(),
  accumulatedSeconds: z.number().int(),
  pausedAt: z.coerce.date().nullable(),
  setupStartTime: z.coerce.date().nullable(),
  setupEndTime: z.coerce.date().nullable(),
  runStartTime: z.coerce.date().nullable(),
  runEndTime: z.coerce.date().nullable(),
  defectReason: z.string().nullable(),
  status: z.string(),
  operatorId: z.string().nullable(),
  notes: z.string().nullable(),
  metadata: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductionLogCreateInputSchema = /* @__PURE__ */ ProductionLogSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ machineId: true, stepId: true, routingStepId: true, shift: true, qtyOk: true, qtyNg: true, unitCost: true, setupCost: true, pieceRateWage: true, setupMinutes: true, breakMinutes: true, waitMinutes: true, stopLossMinutes: true, operatorTeam: true, printSize: true, toolingCode: true, accumulatedSeconds: true, pausedAt: true, setupStartTime: true, setupEndTime: true, runStartTime: true, runEndTime: true, defectReason: true, status: true, operatorId: true, notes: true, metadata: true });
export type ProductionLogRow = z.infer<typeof ProductionLogSchema>;
export type ProductionLogCreateInput = z.infer<typeof ProductionLogCreateInputSchema>;

export const WIPTransferSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  transferId: z.string(),
  fromStep: z.string(),
  toStep: z.string(),
  fromRoutingStepId: z.string().nullable(),
  toRoutingStepId: z.string().nullable(),
  productionOrderId: z.string(),
  quantity: z.number(),
  receivedQty: z.number().int().nullable(),
  rejectedQty: z.number().int().nullable(),
  rejectionReason: z.string().nullable(),
  palletCode: z.string().nullable(),
  senderId: z.string().nullable(),
  senderName: z.string().nullable(),
  receiverId: z.string().nullable(),
  receiverName: z.string().nullable(),
  shift: z.string().nullable(),
  transferDate: z.coerce.date(),
  receivedAt: z.coerce.date().nullable(),
  status: z.string(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const WIPTransferCreateInputSchema = /* @__PURE__ */ WIPTransferSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ fromRoutingStepId: true, toRoutingStepId: true, receivedQty: true, rejectedQty: true, rejectionReason: true, palletCode: true, senderId: true, senderName: true, receiverId: true, receiverName: true, shift: true, receivedAt: true, status: true, notes: true });
export type WIPTransferRow = z.infer<typeof WIPTransferSchema>;
export type WIPTransferCreateInput = z.infer<typeof WIPTransferCreateInputSchema>;

export const QCTemplateSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  version: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  scope: z.string(),
  machineType: MachineTypeEnumSchema.nullable(),
  materialType: z.string().nullable(),
  processStage: z.string().nullable(),
  productTypeId: z.string().nullable(),
  samplingRule: z.string().nullable(),
  aqlCritical: z.number().nullable(),
  aqlMajor: z.number().nullable(),
  aqlMinor: z.number().nullable(),
  status: z.string(),
  schema: jsonValueSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const QCTemplateCreateInputSchema = /* @__PURE__ */ QCTemplateSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ version: true, description: true, scope: true, machineType: true, materialType: true, processStage: true, productTypeId: true, samplingRule: true, aqlCritical: true, aqlMajor: true, aqlMinor: true });
export type QCTemplateRow = z.infer<typeof QCTemplateSchema>;
export type QCTemplateCreateInput = z.infer<typeof QCTemplateCreateInputSchema>;

export const QCRecordSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  recordId: z.string(),
  productionOrderId: z.string(),
  stepId: z.string(),
  inspector: z.string(),
  time: z.coerce.date(),
  status: QCStatusEnumSchema,
  checks: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QCRecordCreateInputSchema = /* @__PURE__ */ QCRecordSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ checks: true, notes: true });
export type QCRecordRow = z.infer<typeof QCRecordSchema>;
export type QCRecordCreateInput = z.infer<typeof QCRecordCreateInputSchema>;

export const QCCheckSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  checkId: z.string(),
  orderId: z.string().nullable(),
  productName: z.string().nullable(),
  checkTime: z.coerce.date(),
  inspectorId: z.string(),
  status: QCStatusEnumSchema,
  checklist: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QCCheckCreateInputSchema = /* @__PURE__ */ QCCheckSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ orderId: true, productName: true, checklist: true, notes: true });
export type QCCheckRow = z.infer<typeof QCCheckSchema>;
export type QCCheckCreateInput = z.infer<typeof QCCheckCreateInputSchema>;

export const QCProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  processId: z.string(),
  productionOrderId: z.string(),
  componentId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  stageProcessId: z.string().nullable(),
  processStage: z.string().nullable(),
  processStep: z.string(),
  machineId: z.string().nullable(),
  productId: z.string().nullable(),
  customerId: z.string().nullable(),
  checkType: z.string().nullable(),
  reportStage: z.string().nullable(),
  setupResult: z.string().nullable(),
  setupCheckedAt: z.coerce.date().nullable(),
  setupSampleQty: z.number().int().nullable(),
  setupDefectQty: z.number().int().nullable(),
  setupNotes: z.string().nullable(),
  patrolLogs: jsonValueSchema.nullable(),
  defectDetails: jsonValueSchema.nullable(),
  qtyPassed: z.number().int().nullable(),
  attachments: jsonValueSchema.nullable(),
  sampleFrequency: z.string().nullable(),
  patrolRound: z.number().int().nullable(),
  shift: z.string().nullable(),
  operatorName: z.string().nullable(),
  lotSize: z.number().int().nullable(),
  aqlLevel: z.string().nullable(),
  qtySample: z.number().int().nullable(),
  qtyDefect: z.number().int().nullable(),
  criticalDefects: z.number().int(),
  majorDefects: z.number().int(),
  minorDefects: z.number().int(),
  checkTime: z.coerce.date(),
  inspectorId: z.string(),
  status: QCStatusEnumSchema,
  checklist: jsonValueSchema.nullable(),
  technicalParams: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  templateId: z.string().nullable(),
  machineLogId: z.string().nullable(),
  fourMChecklist: jsonValueSchema.nullable(),
  referenceSampleNo: z.string().nullable(),
  sampleRef: z.string().nullable(),
  sampleApprovedAt: z.coerce.date().nullable(),
  sampleApprovedBy: z.string().nullable(),
  sampleShiftLead: z.string().nullable(),
  releasedAt: z.coerce.date().nullable(),
  releasedBy: z.string().nullable(),
  inspectionMode: z.string().nullable(),
  aqlMajor: z.number().nullable(),
  aqlMinor: z.number().nullable(),
  acMajor: z.number().int().nullable(),
  reMajor: z.number().int().nullable(),
  acMinor: z.number().int().nullable(),
  reMinor: z.number().int().nullable(),
  overrideReason: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QCProcessCreateInputSchema = /* @__PURE__ */ QCProcessSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ componentId: true, routingStepId: true, stageProcessId: true, processStage: true, machineId: true, productId: true, customerId: true, checkType: true, reportStage: true, setupResult: true, setupCheckedAt: true, setupSampleQty: true, setupDefectQty: true, setupNotes: true, patrolLogs: true, defectDetails: true, qtyPassed: true, attachments: true, sampleFrequency: true, patrolRound: true, shift: true, operatorName: true, lotSize: true, aqlLevel: true, qtySample: true, qtyDefect: true, criticalDefects: true, majorDefects: true, minorDefects: true, checklist: true, technicalParams: true, notes: true, templateId: true, machineLogId: true, fourMChecklist: true, referenceSampleNo: true, sampleRef: true, sampleApprovedAt: true, sampleApprovedBy: true, sampleShiftLead: true, releasedAt: true, releasedBy: true, inspectionMode: true, aqlMajor: true, aqlMinor: true, acMajor: true, reMajor: true, acMinor: true, reMinor: true, overrideReason: true });
export type QCProcessRow = z.infer<typeof QCProcessSchema>;
export type QCProcessCreateInput = z.infer<typeof QCProcessCreateInputSchema>;

export const QCMaterialSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  materialQcId: z.string(),
  materialId: z.string(),
  materialCategory: z.string().nullable(),
  vendorId: z.string().nullable(),
  batchNumber: z.string().nullable(),
  purchaseOrderId: z.string().nullable(),
  receiptDocId: z.string().nullable(),
  receiptDocNo: z.string().nullable(),
  supplierName: z.string().nullable(),
  templateId: z.string().nullable(),
  qtyReceived: z.number().nullable(),
  qtyInspected: z.number().nullable(),
  qtyAccepted: z.number().nullable(),
  qtyRejected: z.number().nullable(),
  lotSize: z.number().int().nullable(),
  sampleSize: z.number().int().nullable(),
  okSampleQty: z.number().int().nullable(),
  ngSampleQty: z.number().int().nullable(),
  attachments: jsonValueSchema.nullable(),
  aqlLevel: z.string().nullable(),
  aqlMajor: z.number().nullable(),
  aqlMinor: z.number().nullable(),
  acMajor: z.number().int().nullable(),
  reMajor: z.number().int().nullable(),
  inspectionMode: z.string().nullable(),
  acMinor: z.number().int().nullable(),
  reMinor: z.number().int().nullable(),
  overrideReason: z.string().nullable(),
  criticalDefects: z.number().int(),
  majorDefects: z.number().int(),
  minorDefects: z.number().int(),
  moisture: z.number().nullable(),
  grammage: z.number().nullable(),
  caliper: z.number().nullable(),
  grainDirection: z.string().nullable(),
  dyneLevel: z.number().nullable(),
  viscosity: z.number().nullable(),
  deltaE: z.number().nullable(),
  coaVerified: z.boolean(),
  checkTime: z.coerce.date(),
  inspectorId: z.string(),
  status: QCStatusEnumSchema,
  checklist: jsonValueSchema.nullable(),
  technicalParams: jsonValueSchema.nullable(),
  defectDetails: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QCMaterialCreateInputSchema = /* @__PURE__ */ QCMaterialSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ materialCategory: true, vendorId: true, batchNumber: true, purchaseOrderId: true, receiptDocId: true, receiptDocNo: true, supplierName: true, templateId: true, qtyReceived: true, qtyInspected: true, qtyAccepted: true, qtyRejected: true, lotSize: true, sampleSize: true, okSampleQty: true, ngSampleQty: true, attachments: true, aqlLevel: true, aqlMajor: true, aqlMinor: true, acMajor: true, reMajor: true, inspectionMode: true, acMinor: true, reMinor: true, overrideReason: true, criticalDefects: true, majorDefects: true, minorDefects: true, moisture: true, grammage: true, caliper: true, grainDirection: true, dyneLevel: true, viscosity: true, deltaE: true, coaVerified: true, checklist: true, technicalParams: true, defectDetails: true, notes: true });
export type QCMaterialRow = z.infer<typeof QCMaterialSchema>;
export type QCMaterialCreateInput = z.infer<typeof QCMaterialCreateInputSchema>;

export const QCFinalSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  finalQcId: z.string(),
  productionOrderId: z.string(),
  orderId: z.string().nullable(),
  productId: z.string().nullable(),
  productTypeId: z.string().nullable(),
  customerId: z.string().nullable(),
  productName: z.string().nullable(),
  checkTime: z.coerce.date(),
  inspectorId: z.string(),
  status: QCStatusEnumSchema,
  qtyChecked: z.number().int().nullable(),
  qtyPassed: z.number().int().nullable(),
  qtyFailed: z.number().int().nullable(),
  aqlLevel: z.string().nullable(),
  aqlStandard: z.number().nullable(),
  lotSize: z.number().int().nullable(),
  sampleSize: z.number().int().nullable(),
  acValue: z.number().int().nullable(),
  reValue: z.number().int().nullable(),
  inspectionMode: z.string().nullable(),
  aqlMinor: z.number().nullable(),
  acMinor: z.number().int().nullable(),
  reMinor: z.number().int().nullable(),
  referenceSample: z.string().nullable(),
  overrideReason: z.string().nullable(),
  packagingCheck: z.boolean().nullable(),
  actualLength: z.number().nullable(),
  actualWidth: z.number().nullable(),
  actualHeight: z.number().nullable(),
  moisture: z.number().nullable(),
  barcodeGrade: z.string().nullable(),
  dropTestResult: z.string().nullable(),
  fittingTestResult: z.string().nullable(),
  checklist: jsonValueSchema.nullable(),
  technicalParams: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  templateId: z.string().nullable(),
  okSampleQty: z.number().int().nullable(),
  ngSampleQty: z.number().int().nullable(),
  criticalDefects: z.number().int(),
  majorDefects: z.number().int(),
  minorDefects: z.number().int(),
  defectDetails: jsonValueSchema.nullable(),
  coaNo: z.string().nullable(),
  coaIssuedAt: z.coerce.date().nullable(),
  attachments: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const QCFinalCreateInputSchema = /* @__PURE__ */ QCFinalSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ orderId: true, productId: true, productTypeId: true, customerId: true, productName: true, qtyChecked: true, qtyPassed: true, qtyFailed: true, aqlLevel: true, aqlStandard: true, lotSize: true, sampleSize: true, acValue: true, reValue: true, inspectionMode: true, aqlMinor: true, acMinor: true, reMinor: true, referenceSample: true, overrideReason: true, packagingCheck: true, actualLength: true, actualWidth: true, actualHeight: true, moisture: true, barcodeGrade: true, dropTestResult: true, fittingTestResult: true, checklist: true, technicalParams: true, notes: true, templateId: true, okSampleQty: true, ngSampleQty: true, criticalDefects: true, majorDefects: true, minorDefects: true, defectDetails: true, coaNo: true, coaIssuedAt: true, attachments: true });
export type QCFinalRow = z.infer<typeof QCFinalSchema>;
export type QCFinalCreateInput = z.infer<typeof QCFinalCreateInputSchema>;

export const PackingSlipSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  qcFinalId: z.string(),
  productionOrderId: z.string(),
  status: z.string(),
  qtyFromQc: z.number().int(),
  qtyPacked: z.number().int().nullable(),
  qtyDefect: z.number().int(),
  packerId: z.string().nullable(),
  packedAt: z.coerce.date().nullable(),
  pcsPerBox: z.number().int().nullable(),
  totalBoxes: z.number().int().nullable(),
  boxesPerPallet: z.number().int().nullable(),
  totalPallets: z.number().int().nullable(),
  boxDimensions: z.string().nullable(),
  boxGrossWeight: z.number().nullable(),
  lotNumber: z.string().nullable(),
  targetWarehouseId: z.string().nullable(),
  receiptDocId: z.string().nullable(),
  receiptDocNo: z.string().nullable(),
  readyForDispatchAt: z.coerce.date().nullable(),
  readyForDispatchById: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PackingSlipCreateInputSchema = /* @__PURE__ */ PackingSlipSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ status: true, qtyFromQc: true, qtyPacked: true, qtyDefect: true, packerId: true, packedAt: true, pcsPerBox: true, totalBoxes: true, boxesPerPallet: true, totalPallets: true, boxDimensions: true, boxGrossWeight: true, lotNumber: true, targetWarehouseId: true, receiptDocId: true, receiptDocNo: true, readyForDispatchAt: true, readyForDispatchById: true, notes: true });
export type PackingSlipRow = z.infer<typeof PackingSlipSchema>;
export type PackingSlipCreateInput = z.infer<typeof PackingSlipCreateInputSchema>;

export const NCRSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  ncrId: z.string(),
  source: NCRSourceEnumSchema,
  sourceType: z.string().nullable(),
  relatedId: z.string().nullable(),
  relatedType: z.string().nullable(),
  qcMaterialId: z.string().nullable(),
  qcProcessId: z.string().nullable(),
  qcFinalId: z.string().nullable(),
  productionOrderId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  processStage: z.string().nullable(),
  machineId: z.string().nullable(),
  materialId: z.string().nullable(),
  vendorId: z.string().nullable(),
  productId: z.string().nullable(),
  customerId: z.string().nullable(),
  batchNumber: z.string().nullable(),
  lotSize: z.number().int().nullable(),
  checkedQty: z.number().int().nullable(),
  sampleSize: z.number().int().nullable(),
  okQty: z.number().int().nullable(),
  defectQty: z.number().int().nullable(),
  unit: z.string().nullable(),
  defectDetails: jsonValueSchema.nullable(),
  costBreakdown: jsonValueSchema.nullable(),
  resolutionResult: z.string().nullable(),
  attachments: jsonValueSchema.nullable(),
  defectCategory6M: z.string().nullable(),
  disposition: z.string().nullable(),
  containmentAction: z.string().nullable(),
  actionPlan: z.string().nullable(),
  issueSummary: z.string(),
  rootCause: z.string().nullable(),
  whyAnalysis: jsonValueSchema.nullable(),
  failedCriteria: jsonValueSchema.nullable(),
  severity: NCRSeverityEnumSchema,
  status: NCRStatusEnumSchema,
  responsibleDept: z.string().nullable(),
  costImpact: z.number().nullable(),
  ownerId: z.string().nullable(),
  capaPlan: z.string().nullable(),
  preventiveAction: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const NCRCreateInputSchema = /* @__PURE__ */ NCRSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ sourceType: true, relatedId: true, relatedType: true, qcMaterialId: true, qcProcessId: true, qcFinalId: true, productionOrderId: true, routingStepId: true, processStage: true, machineId: true, materialId: true, vendorId: true, productId: true, customerId: true, batchNumber: true, lotSize: true, checkedQty: true, sampleSize: true, okQty: true, defectQty: true, unit: true, defectDetails: true, costBreakdown: true, resolutionResult: true, attachments: true, defectCategory6M: true, disposition: true, containmentAction: true, actionPlan: true, rootCause: true, whyAnalysis: true, failedCriteria: true, responsibleDept: true, costImpact: true, ownerId: true, capaPlan: true, preventiveAction: true });
export type NCRRow = z.infer<typeof NCRSchema>;
export type NCRCreateInput = z.infer<typeof NCRCreateInputSchema>;

export const VendorSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  type: z.string().nullable(),
  group: z.string().nullable(),
  shortName: z.string().nullable(),
  name: z.string(),
  address: z.string().nullable(),
  taxCode: z.string().nullable(),
  representative: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  fanpage: z.string().nullable(),
  logo: z.string().nullable(),
  panel: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
  bankAccountHolder: z.string().nullable(),
  bankName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  deliveryMethod: z.string().nullable(),
  deliveryAddress: z.string().nullable(),
  transitAddress: z.string().nullable(),
  initialDebt: z.number(),
  currentDebt: z.number(),
  openingDebt: z.number(),
  orderValue: z.number(),
  deliveryValue: z.number(),
  paidAmount: z.number(),
  remainingDebt: z.number(),
  notes: z.string().nullable(),
  tags: z.string().nullable(),
  status: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const VendorCreateInputSchema = /* @__PURE__ */ VendorSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ type: true, group: true, shortName: true, address: true, taxCode: true, representative: true, phone: true, website: true, fanpage: true, logo: true, panel: true, bankAccountNumber: true, bankAccountHolder: true, bankName: true, contactPerson: true, mobile: true, email: true, deliveryMethod: true, deliveryAddress: true, transitAddress: true, initialDebt: true, currentDebt: true, openingDebt: true, orderValue: true, deliveryValue: true, paidAmount: true, remainingDebt: true, notes: true, tags: true, status: true });
export type VendorRow = z.infer<typeof VendorSchema>;
export type VendorCreateInput = z.infer<typeof VendorCreateInputSchema>;

export const OutsourcePOSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  poId: z.string(),
  orderId: z.string().nullable(),
  poReference: z.string().nullable(),
  deploymentModel: DeploymentModelEnumSchema.nullable(),
  vendorId: z.string(),
  productionOrderId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  routingStepIds: z.array(z.string()),
  processId: z.string().nullable(),
  productName: z.string().nullable(),
  productSpecs: z.string().nullable(),
  notes: z.string().nullable(),
  category: z.string().nullable(),
  customerId: z.string().nullable(),
  qtyOrdered: z.number().int(),
  allowanceRate: z.number().nullable(),
  allowanceQty: z.number().int(),
  qtySentTotal: z.number().int().nullable(),
  qtyReceived: z.number().int(),
  qtyAccepted: z.number().int(),
  qtyRejected: z.number().int(),
  packedQty: z.number().int(),
  exportedQty: z.number().int(),
  remainingQty: z.number().int(),
  unitPrice: z.number(),
  totalAmount: z.number(),
  materialIssued: jsonValueSchema.nullable(),
  issueDate: z.coerce.date(),
  expectedDate: z.coerce.date(),
  actualReceiveDate: z.coerce.date().nullable(),
  status: OutsourcePOStatusEnumSchema,
  paymentStatus: PaymentStatusEnumSchema,
  progressLogs: jsonValueSchema.nullable(),
  receipts: jsonValueSchema.nullable(),
  paymentRecords: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
});
export const OutsourcePOCreateInputSchema = /* @__PURE__ */ OutsourcePOSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true, createdBy: true }).partial({ orderId: true, poReference: true, deploymentModel: true, productionOrderId: true, routingStepId: true, routingStepIds: true, processId: true, productName: true, productSpecs: true, notes: true, category: true, customerId: true, allowanceRate: true, allowanceQty: true, qtySentTotal: true, qtyReceived: true, qtyAccepted: true, qtyRejected: true, packedQty: true, exportedQty: true, remainingQty: true, materialIssued: true, actualReceiveDate: true, paymentStatus: true, progressLogs: true, receipts: true, paymentRecords: true });
export type OutsourcePORow = z.infer<typeof OutsourcePOSchema>;
export type OutsourcePOCreateInput = z.infer<typeof OutsourcePOCreateInputSchema>;

export const WarehouseSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  managerName: z.string().nullable(),
  phone: z.string().nullable(),
  address: z.string().nullable(),
  status: z.string(),
  notes: z.string().nullable(),
  allowNegativeStock: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const WarehouseCreateInputSchema = /* @__PURE__ */ WarehouseSchema.omit({ id: true, isDeleted: true, deletedAt: true, createdAt: true, updatedAt: true }).partial({ type: true, managerName: true, phone: true, address: true, status: true, notes: true, allowNegativeStock: true });
export type WarehouseRow = z.infer<typeof WarehouseSchema>;
export type WarehouseCreateInput = z.infer<typeof WarehouseCreateInputSchema>;

export const WarehouseLocationSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  warehouseId: z.string(),
  code: z.string(),
  name: z.string(),
  aisle: z.string().nullable(),
  rack: z.string().nullable(),
  shelf: z.string().nullable(),
  bin: z.string().nullable(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const WarehouseLocationCreateInputSchema = /* @__PURE__ */ WarehouseLocationSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ aisle: true, rack: true, shelf: true, bin: true, status: true });
export type WarehouseLocationRow = z.infer<typeof WarehouseLocationSchema>;
export type WarehouseLocationCreateInput = z.infer<typeof WarehouseLocationCreateInputSchema>;

export const InventoryBalanceSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  warehouseId: z.string(),
  locationId: z.string().nullable(),
  itemType: z.string(),
  itemId: z.string(),
  lotNumber: z.string(),
  expiryDate: z.coerce.date().nullable(),
  qtyOnHand: z.number(),
  qtyReserved: z.number(),
  qtyAvailable: z.number(),
  minQty: z.number().nullable(),
  maxQty: z.number().nullable(),
  avgPrice: z.number().nullable(),
  updatedAt: z.coerce.date(),
});
export const InventoryBalanceCreateInputSchema = /* @__PURE__ */ InventoryBalanceSchema.omit({ id: true, updatedAt: true }).partial({ locationId: true, lotNumber: true, expiryDate: true, qtyOnHand: true, qtyReserved: true, qtyAvailable: true, minQty: true, maxQty: true, avgPrice: true });
export type InventoryBalanceRow = z.infer<typeof InventoryBalanceSchema>;
export type InventoryBalanceCreateInput = z.infer<typeof InventoryBalanceCreateInputSchema>;

export const InventoryTransactionSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  transactionNo: z.string(),
  docId: z.string().nullable(),
  warehouseId: z.string(),
  locationId: z.string().nullable(),
  itemType: z.string(),
  itemId: z.string(),
  itemName: z.string().nullable(),
  itemCode: z.string().nullable(),
  txType: z.string(),
  quantity: z.number(),
  unitPrice: z.number().nullable(),
  lotNumber: z.string().nullable(),
  expiryDate: z.coerce.date().nullable(),
  amount: z.number().nullable(),
  balanceAfter: z.number(),
  referenceType: z.string().nullable(),
  referenceNo: z.string().nullable(),
  notes: z.string().nullable(),
  txDate: z.coerce.date(),
  createdAt: z.coerce.date(),
});
export const InventoryTransactionCreateInputSchema = /* @__PURE__ */ InventoryTransactionSchema.omit({ id: true, createdAt: true }).partial({ docId: true, locationId: true, itemName: true, itemCode: true, unitPrice: true, lotNumber: true, expiryDate: true, amount: true, referenceType: true, referenceNo: true, notes: true, txDate: true });
export type InventoryTransactionRow = z.infer<typeof InventoryTransactionSchema>;
export type InventoryTransactionCreateInput = z.infer<typeof InventoryTransactionCreateInputSchema>;

export const InventoryDocumentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  docNo: z.string(),
  docType: DocumentTypeEnumSchema,
  status: DocumentStatusEnumSchema,
  sourceWarehouseId: z.string().nullable(),
  targetWarehouseId: z.string().nullable(),
  vendorId: z.string().nullable(),
  customerId: z.string().nullable(),
  productionOrderId: z.string().nullable(),
  orderId: z.string().nullable(),
  deliveryId: z.string().nullable(),
  docDate: z.coerce.date(),
  referenceDoc: z.string().nullable(),
  driverName: z.string().nullable(),
  vehiclePlate: z.string().nullable(),
  expectedDate: z.coerce.date().nullable(),
  actualDate: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
  approvedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const InventoryDocumentCreateInputSchema = /* @__PURE__ */ InventoryDocumentSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true }).partial({ status: true, sourceWarehouseId: true, targetWarehouseId: true, vendorId: true, customerId: true, productionOrderId: true, orderId: true, deliveryId: true, docDate: true, referenceDoc: true, driverName: true, vehiclePlate: true, expectedDate: true, actualDate: true, notes: true, approvedBy: true });
export type InventoryDocumentRow = z.infer<typeof InventoryDocumentSchema>;
export type InventoryDocumentCreateInput = z.infer<typeof InventoryDocumentCreateInputSchema>;

export const InventoryDocumentLineSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  documentId: z.string(),
  itemType: z.string(),
  itemId: z.string(),
  itemCode: z.string().nullable(),
  itemName: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.number().nullable(),
  amount: z.number().nullable(),
  lotNumber: z.string().nullable(),
  expiryDate: z.coerce.date().nullable(),
  locationId: z.string().nullable(),
  notes: z.string().nullable(),
});
export const InventoryDocumentLineCreateInputSchema = /* @__PURE__ */ InventoryDocumentLineSchema.omit({ id: true }).partial({ itemCode: true, itemName: true, unit: true, unitPrice: true, amount: true, lotNumber: true, expiryDate: true, locationId: true, notes: true });
export type InventoryDocumentLineRow = z.infer<typeof InventoryDocumentLineSchema>;
export type InventoryDocumentLineCreateInput = z.infer<typeof InventoryDocumentLineCreateInputSchema>;

export const ToolingItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: z.string().nullable(),
  unit: z.string().nullable(),
  description: z.string().nullable(),
  status: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ToolingItemCreateInputSchema = /* @__PURE__ */ ToolingItemSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ category: true, unit: true, description: true, status: true });
export type ToolingItemRow = z.infer<typeof ToolingItemSchema>;
export type ToolingItemCreateInput = z.infer<typeof ToolingItemCreateInputSchema>;

export const PurchaseOrderSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  poId: z.string(),
  vendorId: z.string(),
  totalAmount: z.number(),
  orderDate: z.coerce.date(),
  expectedDate: z.coerce.date().nullable(),
  receivedDate: z.coerce.date().nullable(),
  status: PurchaseOrderStatusEnumSchema,
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
});
export const PurchaseOrderCreateInputSchema = /* @__PURE__ */ PurchaseOrderSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true, createdBy: true }).partial({ expectedDate: true, receivedDate: true, notes: true });
export type PurchaseOrderRow = z.infer<typeof PurchaseOrderSchema>;
export type PurchaseOrderCreateInput = z.infer<typeof PurchaseOrderCreateInputSchema>;

export const PurchaseOrderItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  purchaseOrderId: z.string(),
  materialId: z.string(),
  productionOrderId: z.string().nullable(),
  materialPlanId: z.string().nullable(),
  quantity: z.number(),
  unitPrice: z.number(),
  receivedQty: z.number(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PurchaseOrderItemCreateInputSchema = /* @__PURE__ */ PurchaseOrderItemSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productionOrderId: true, materialPlanId: true, receivedQty: true, notes: true });
export type PurchaseOrderItemRow = z.infer<typeof PurchaseOrderItemSchema>;
export type PurchaseOrderItemCreateInput = z.infer<typeof PurchaseOrderItemCreateInputSchema>;

export const PurchaseRequestSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  requestId: z.string(),
  materialId: z.string().nullable(),
  productionOrderId: z.string().nullable(),
  materialPlanId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  machineId: z.string().nullable(),
  quantity: z.number(),
  requiredDate: z.coerce.date(),
  reason: z.string().nullable(),
  requesterId: z.string(),
  status: PurchaseRequestStatusEnumSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PurchaseRequestCreateInputSchema = /* @__PURE__ */ PurchaseRequestSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ materialId: true, productionOrderId: true, materialPlanId: true, routingStepId: true, machineId: true, reason: true });
export type PurchaseRequestRow = z.infer<typeof PurchaseRequestSchema>;
export type PurchaseRequestCreateInput = z.infer<typeof PurchaseRequestCreateInputSchema>;

export const DeliverySchema = /* @__PURE__ */ z.object({
  id: z.string(),
  deliveryId: z.string(),
  customerId: z.string(),
  orderId: z.string().nullable(),
  salesUserId: z.string().nullable(),
  deliveryDate: z.coerce.date(),
  address: z.string().nullable(),
  transshipmentAddr: z.string().nullable(),
  deliveryType: z.string().nullable(),
  deliveryFee: z.number().nullable(),
  receiverName: z.string().nullable(),
  receiverPhone: z.string().nullable(),
  driverName: z.string().nullable(),
  vehicleNumber: z.string().nullable(),
  status: DeliveryStatusEnumSchema,
  notes: z.string().nullable(),
  totalAmount: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  debtReconciliationId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
  createdBy: z.string().nullable(),
});
export const DeliveryCreateInputSchema = /* @__PURE__ */ DeliverySchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true, createdBy: true }).partial({ orderId: true, salesUserId: true, address: true, transshipmentAddr: true, deliveryType: true, deliveryFee: true, receiverName: true, receiverPhone: true, driverName: true, vehicleNumber: true, notes: true, totalAmount: true, debtReconciliationId: true });
export type DeliveryRow = z.infer<typeof DeliverySchema>;
export type DeliveryCreateInput = z.infer<typeof DeliveryCreateInputSchema>;

export const DeliveryItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  deliveryId: z.string(),
  productId: z.string().nullable(),
  productName: z.string(),
  poNumber: z.string().nullable(),
  soNumber: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number().int(),
  deliveredQty: z.number().int(),
  lossQty: z.number().int(),
  countedQty: z.number().int(),
  unitPrice: z.number().nullable(),
  amount: z.number().nullable(),
  orderDetailId: z.string().nullable(),
  isServiceItem: z.boolean(),
  serviceType: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const DeliveryItemCreateInputSchema = /* @__PURE__ */ DeliveryItemSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productId: true, poNumber: true, soNumber: true, unit: true, quantity: true, deliveredQty: true, lossQty: true, countedQty: true, unitPrice: true, amount: true, orderDetailId: true, isServiceItem: true, serviceType: true, notes: true });
export type DeliveryItemRow = z.infer<typeof DeliveryItemSchema>;
export type DeliveryItemCreateInput = z.infer<typeof DeliveryItemCreateInputSchema>;

export const PlateSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  plateId: z.string(),
  name: z.string(),
  productId: z.string().nullable(),
  productName: z.string().nullable(),
  customerId: z.string().nullable(),
  size: z.string().nullable(),
  quantity: z.number().int(),
  colors: z.array(z.string()),
  status: ToolingStatusEnumSchema,
  location: z.string().nullable(),
  image: z.string().nullable(),
  notes: z.string().nullable(),
  attachments: jsonValueSchema.nullable(),
  designFileUrl: z.string().nullable(),
  ctpFileUrl: z.string().nullable(),
  version: z.number().int(),
  expectedDate: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  layoutId: z.string().nullable(),
  maxImpressions: z.number().int(),
  usageCount: z.number().int(),
  storageZone: z.string().nullable(),
  storageRack: z.string().nullable(),
  storageShelf: z.string().nullable(),
  proofStatus: z.string().nullable(),
  proofApprovedAt: z.coerce.date().nullable(),
  proofApprover: z.string().nullable(),
  proofNotes: z.string().nullable(),
  proofSampleUrl: z.string().nullable(),
  activeMachineId: z.string().nullable(),
  activeMachineName: z.string().nullable(),
  mountedAt: z.coerce.date().nullable(),
  mountedBy: z.string().nullable(),
  damageReason: z.string().nullable(),
  damagedColors: z.array(z.string()),
  remakeCount: z.number().int(),
  printFinishedAt: z.coerce.date().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PlateCreateInputSchema = /* @__PURE__ */ PlateSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productId: true, productName: true, customerId: true, size: true, quantity: true, colors: true, status: true, location: true, image: true, notes: true, attachments: true, designFileUrl: true, ctpFileUrl: true, version: true, expectedDate: true, layoutId: true, maxImpressions: true, usageCount: true, storageZone: true, storageRack: true, storageShelf: true, proofStatus: true, proofApprovedAt: true, proofApprover: true, proofNotes: true, proofSampleUrl: true, activeMachineId: true, activeMachineName: true, mountedAt: true, mountedBy: true, damageReason: true, damagedColors: true, remakeCount: true, printFinishedAt: true });
export type PlateRow = z.infer<typeof PlateSchema>;
export type PlateCreateInput = z.infer<typeof PlateCreateInputSchema>;

export const CtpPlateLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  logDate: z.coerce.date(),
  productId: z.string().nullable(),
  productName: z.string(),
  poReference: z.string().nullable(),
  importQty: z.number().int(),
  usedQty: z.number().int(),
  stockQty: z.number().int(),
  plateType: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CtpPlateLogCreateInputSchema = /* @__PURE__ */ CtpPlateLogSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productId: true, poReference: true, importQty: true, usedQty: true, stockQty: true, plateType: true, notes: true });
export type CtpPlateLogRow = z.infer<typeof CtpPlateLogSchema>;
export type CtpPlateLogCreateInput = z.infer<typeof CtpPlateLogCreateInputSchema>;

export const PlateUsageHistorySchema = /* @__PURE__ */ z.object({
  id: z.string(),
  plateId: z.string(),
  productionOrderId: z.string().nullable(),
  poCode: z.string().nullable(),
  machineId: z.string().nullable(),
  machineName: z.string().nullable(),
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  impressions: z.number().int(),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export const PlateUsageHistoryCreateInputSchema = /* @__PURE__ */ PlateUsageHistorySchema.omit({ id: true, createdAt: true }).partial({ productionOrderId: true, poCode: true, machineId: true, machineName: true, operatorId: true, operatorName: true, impressions: true, startedAt: true, completedAt: true, notes: true });
export type PlateUsageHistoryRow = z.infer<typeof PlateUsageHistorySchema>;
export type PlateUsageHistoryCreateInput = z.infer<typeof PlateUsageHistoryCreateInputSchema>;

export const DieCutSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  dieCutId: z.string(),
  name: z.string(),
  productId: z.string().nullable(),
  productName: z.string().nullable(),
  customerId: z.string().nullable(),
  size: z.string().nullable(),
  type: ToolingTypeEnumSchema.nullable(),
  quantity: z.number().int(),
  status: ToolingStatusEnumSchema,
  location: z.string().nullable(),
  machine: z.string().nullable(),
  image: z.string().nullable(),
  images: jsonValueSchema.nullable(),
  notes: z.string().nullable(),
  attachments: jsonValueSchema.nullable(),
  usageCount: z.number().int(),
  maxImpressions: z.number().int(),
  maintenanceInterval: z.number().int(),
  lastMaintenanceImpressions: z.number().int(),
  storageZone: z.string().nullable(),
  storageRack: z.string().nullable(),
  storageShelf: z.string().nullable(),
  storageBin: z.string().nullable(),
  version: z.number().int(),
  expectedDate: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  layoutId: z.string().nullable(),
  isShared: z.boolean(),
  dieLength: z.number().nullable(),
  dieWidth: z.number().nullable(),
  productLength: z.number().nullable(),
  productWidth: z.number().nullable(),
  productHeight: z.number().nullable(),
  ups: z.number().int().nullable(),
  discardReason: z.string().nullable(),
  discardedAt: z.coerce.date().nullable(),
  discardedBy: z.string().nullable(),
  replacesId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const DieCutCreateInputSchema = /* @__PURE__ */ DieCutSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productId: true, productName: true, customerId: true, size: true, type: true, quantity: true, status: true, location: true, machine: true, image: true, images: true, notes: true, attachments: true, usageCount: true, maxImpressions: true, maintenanceInterval: true, lastMaintenanceImpressions: true, storageZone: true, storageRack: true, storageShelf: true, storageBin: true, version: true, expectedDate: true, layoutId: true, isShared: true, dieLength: true, dieWidth: true, productLength: true, productWidth: true, productHeight: true, ups: true, discardReason: true, discardedAt: true, discardedBy: true, replacesId: true });
export type DieCutRow = z.infer<typeof DieCutSchema>;
export type DieCutCreateInput = z.infer<typeof DieCutCreateInputSchema>;

export const DieCutUsageHistorySchema = /* @__PURE__ */ z.object({
  id: z.string(),
  dieCutId: z.string(),
  productionOrderId: z.string().nullable(),
  poCode: z.string().nullable(),
  orderDetailId: z.string().nullable(),
  machineId: z.string().nullable(),
  machineName: z.string().nullable(),
  operatorId: z.string().nullable(),
  operatorName: z.string().nullable(),
  impressions: z.number().int(),
  startedAt: z.coerce.date(),
  completedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export const DieCutUsageHistoryCreateInputSchema = /* @__PURE__ */ DieCutUsageHistorySchema.omit({ id: true, createdAt: true }).partial({ productionOrderId: true, poCode: true, orderDetailId: true, machineId: true, machineName: true, operatorId: true, operatorName: true, impressions: true, startedAt: true, completedAt: true, notes: true });
export type DieCutUsageHistoryRow = z.infer<typeof DieCutUsageHistorySchema>;
export type DieCutUsageHistoryCreateInput = z.infer<typeof DieCutUsageHistoryCreateInputSchema>;

export const DieCutLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  dieCutId: z.string(),
  oldStatus: ToolingStatusEnumSchema.nullable(),
  newStatus: ToolingStatusEnumSchema,
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  note: z.string().nullable(),
});
export const DieCutLogCreateInputSchema = /* @__PURE__ */ DieCutLogSchema.omit({ id: true, createdBy: true, createdAt: true }).partial({ oldStatus: true, note: true });
export type DieCutLogRow = z.infer<typeof DieCutLogSchema>;
export type DieCutLogCreateInput = z.infer<typeof DieCutLogCreateInputSchema>;

export const LayoutSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  layoutId: z.string(),
  name: z.string(),
  productName: z.string().nullable(),
  sheetSize: z.string().nullable(),
  ups: z.number().int().nullable(),
  gripperMargin: z.string().nullable(),
  notes: z.string().nullable(),
  fileUrl: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const LayoutCreateInputSchema = /* @__PURE__ */ LayoutSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productName: true, sheetSize: true, ups: true, gripperMargin: true, notes: true, fileUrl: true });
export type LayoutRow = z.infer<typeof LayoutSchema>;
export type LayoutCreateInput = z.infer<typeof LayoutCreateInputSchema>;

export const ChangelogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  version: z.string(),
  date: z.coerce.date(),
  changes: jsonValueSchema,
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ChangelogCreateInputSchema = /* @__PURE__ */ ChangelogSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true });
export type ChangelogRow = z.infer<typeof ChangelogSchema>;
export type ChangelogCreateInput = z.infer<typeof ChangelogCreateInputSchema>;

export const UserGuideSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  section: z.string(),
  title: z.string(),
  content: z.string(),
  order: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const UserGuideCreateInputSchema = /* @__PURE__ */ UserGuideSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true });
export type UserGuideRow = z.infer<typeof UserGuideSchema>;
export type UserGuideCreateInput = z.infer<typeof UserGuideCreateInputSchema>;

export const CommentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  content: z.string(),
  authorId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CommentCreateInputSchema = /* @__PURE__ */ CommentSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true });
export type CommentRow = z.infer<typeof CommentSchema>;
export type CommentCreateInput = z.infer<typeof CommentCreateInputSchema>;

export const ActivityLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  field: z.string().nullable(),
  oldValue: z.string().nullable(),
  newValue: z.string().nullable(),
  payload: jsonValueSchema.nullable(),
  description: z.string().nullable(),
  userId: z.string().nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ActivityLogCreateInputSchema = /* @__PURE__ */ ActivityLogSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ field: true, oldValue: true, newValue: true, payload: true, description: true, userId: true });
export type ActivityLogRow = z.infer<typeof ActivityLogSchema>;
export type ActivityLogCreateInput = z.infer<typeof ActivityLogCreateInputSchema>;

export const AttachmentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  fileName: z.string(),
  fileUrl: z.string(),
  fileSize: z.number().int().nullable(),
  mimeType: z.string().nullable(),
  uploadedBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const AttachmentCreateInputSchema = /* @__PURE__ */ AttachmentSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ fileSize: true, mimeType: true, uploadedBy: true });
export type AttachmentRow = z.infer<typeof AttachmentSchema>;
export type AttachmentCreateInput = z.infer<typeof AttachmentCreateInputSchema>;

export const BackgroundJobSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  jobType: z.string(),
  entityType: z.string().nullable(),
  entityId: z.string().nullable(),
  status: z.string(),
  progress: z.number().int(),
  payload: jsonValueSchema.nullable(),
  result: jsonValueSchema.nullable(),
  error: z.string().nullable(),
  userId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const BackgroundJobCreateInputSchema = /* @__PURE__ */ BackgroundJobSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ entityType: true, entityId: true, status: true, progress: true, payload: true, result: true, error: true, userId: true });
export type BackgroundJobRow = z.infer<typeof BackgroundJobSchema>;
export type BackgroundJobCreateInput = z.infer<typeof BackgroundJobCreateInputSchema>;

export const ConfigCategorySchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  section: z.string(),
  pattern: z.string(),
  fields: jsonValueSchema,
  sortOrder: z.number().int(),
  active: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ConfigCategoryCreateInputSchema = /* @__PURE__ */ ConfigCategorySchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ fields: true, sortOrder: true, active: true });
export type ConfigCategoryRow = z.infer<typeof ConfigCategorySchema>;
export type ConfigCategoryCreateInput = z.infer<typeof ConfigCategoryCreateInputSchema>;

export const ConfigItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  categoryId: z.string(),
  name: z.string(),
  code: z.string().nullable(),
  data: jsonValueSchema,
  sortOrder: z.number().int(),
  active: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ConfigItemCreateInputSchema = /* @__PURE__ */ ConfigItemSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ code: true, data: true, sortOrder: true, active: true });
export type ConfigItemRow = z.infer<typeof ConfigItemSchema>;
export type ConfigItemCreateInput = z.infer<typeof ConfigItemCreateInputSchema>;

export const WorkflowTransitionSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  process: z.string(),
  processLabel: z.string().nullable(),
  fromStatus: z.string(),
  toStatus: z.string(),
  action: z.string(),
  description: z.string().nullable(),
  leadtime: z.number().int().nullable(),
  responsibility: z.string().nullable(),
  lockEdit: z.boolean(),
  autoTransition: z.boolean(),
  fromColor: z.string().nullable(),
  toColor: z.string().nullable(),
  actionColor: z.string().nullable(),
  sortOrder: z.number().int(),
  active: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const WorkflowTransitionCreateInputSchema = /* @__PURE__ */ WorkflowTransitionSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ processLabel: true, description: true, leadtime: true, responsibility: true, lockEdit: true, autoTransition: true, fromColor: true, toColor: true, actionColor: true, sortOrder: true, active: true });
export type WorkflowTransitionRow = z.infer<typeof WorkflowTransitionSchema>;
export type WorkflowTransitionCreateInput = z.infer<typeof WorkflowTransitionCreateInputSchema>;

export const FeedbackSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string().nullable(),
  module: z.string(),
  pageUrl: z.string(),
  type: FeedbackTypeEnumSchema,
  priority: FeedbackPriorityEnumSchema,
  title: z.string(),
  description: z.string().nullable(),
  images: jsonValueSchema.nullable(),
  screenshotBefore: z.string().nullable(),
  screenshotAfter: z.string().nullable(),
  status: FeedbackStatusEnumSchema,
  reportedBy: z.string().nullable(),
  assignedTo: z.string().nullable(),
  devResponse: z.string().nullable(),
  source: z.string(),
  diagnostics: jsonValueSchema.nullable(),
  fingerprint: z.string().nullable(),
  occurrenceCount: z.number().int(),
  affectedUsers: z.number().int(),
  firstSeenAt: z.coerce.date().nullable(),
  lastSeenAt: z.coerce.date().nullable(),
  appVersion: z.string().nullable(),
  userAgent: z.string().nullable(),
  sessionId: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const FeedbackCreateInputSchema = /* @__PURE__ */ FeedbackSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ code: true, module: true, pageUrl: true, type: true, priority: true, description: true, images: true, screenshotBefore: true, screenshotAfter: true, status: true, reportedBy: true, assignedTo: true, devResponse: true, source: true, diagnostics: true, fingerprint: true, occurrenceCount: true, affectedUsers: true, firstSeenAt: true, lastSeenAt: true, appVersion: true, userAgent: true, sessionId: true, requestId: true });
export type FeedbackRow = z.infer<typeof FeedbackSchema>;
export type FeedbackCreateInput = z.infer<typeof FeedbackCreateInputSchema>;

export const UserEventSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  occurredAt: z.coerce.date(),
  receivedAt: z.coerce.date(),
  userId: z.string().nullable(),
  sessionId: z.string().nullable(),
  kind: z.string(),
  name: z.string().max(200),
  level: z.string(),
  status: z.number().int().nullable(),
  durationMs: z.number().int().nullable(),
  requestId: z.string().nullable(),
  payload: jsonValueSchema.nullable(),
  appVersion: z.string().nullable(),
});
export const UserEventCreateInputSchema = /* @__PURE__ */ UserEventSchema.omit({ id: true }).partial({ occurredAt: true, receivedAt: true, userId: true, sessionId: true, level: true, status: true, durationMs: true, requestId: true, payload: true, appVersion: true });
export type UserEventRow = z.infer<typeof UserEventSchema>;
export type UserEventCreateInput = z.infer<typeof UserEventCreateInputSchema>;

export const FeedbackCommentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  feedbackId: z.string(),
  userId: z.string(),
  comment: z.string(),
  images: jsonValueSchema.nullable(),
  oldStatus: z.string().nullable(),
  newStatus: z.string().nullable(),
  meta: jsonValueSchema.nullable(),
  createdAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const FeedbackCommentCreateInputSchema = /* @__PURE__ */ FeedbackCommentSchema.omit({ id: true, createdAt: true, isDeleted: true, deletedAt: true }).partial({ images: true, oldStatus: true, newStatus: true, meta: true });
export type FeedbackCommentRow = z.infer<typeof FeedbackCommentSchema>;
export type FeedbackCommentCreateInput = z.infer<typeof FeedbackCommentCreateInputSchema>;

export const KpiSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string(),
  department: z.string(),
  period: z.string(),
  year: z.number().int(),
  month: z.number().int(),
  revenueTarget: z.number(),
  revenueActual: z.number(),
  ordersTarget: z.number().int(),
  ordersActual: z.number().int(),
  newCustTarget: z.number().int(),
  newCustActual: z.number().int(),
  quotationsTarget: z.number().int(),
  quotationsActual: z.number().int(),
  conversionTarget: z.number(),
  conversionActual: z.number(),
  deliveryTarget: z.number(),
  deliveryActual: z.number(),
  score: z.number(),
  status: KpiStatusEnumSchema,
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const KpiCreateInputSchema = /* @__PURE__ */ KpiSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ revenueTarget: true, revenueActual: true, ordersTarget: true, ordersActual: true, newCustTarget: true, newCustActual: true, quotationsTarget: true, quotationsActual: true, conversionTarget: true, conversionActual: true, deliveryTarget: true, deliveryActual: true, score: true, status: true, notes: true });
export type KpiRow = z.infer<typeof KpiSchema>;
export type KpiCreateInput = z.infer<typeof KpiCreateInputSchema>;

export const ReceiptSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  receiptId: z.string(),
  type: z.string(),
  categoryId: z.string().nullable(),
  sourceType: z.string().nullable(),
  customerId: z.string(),
  amount: z.number(),
  receiptDate: z.coerce.date(),
  paymentMethod: z.string(),
  referenceNumber: z.string().nullable(),
  notes: z.string().nullable(),
  attachments: jsonValueSchema.nullable(),
  status: z.string(),
  debtReconciliationId: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ReceiptCreateInputSchema = /* @__PURE__ */ ReceiptSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ type: true, categoryId: true, sourceType: true, referenceNumber: true, notes: true, attachments: true, status: true, debtReconciliationId: true });
export type ReceiptRow = z.infer<typeof ReceiptSchema>;
export type ReceiptCreateInput = z.infer<typeof ReceiptCreateInputSchema>;

export const DebtReconciliationSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  customerId: z.string(),
  month: z.number().int(),
  year: z.number().int(),
  openingBalance: z.number(),
  totalIncurred: z.number(),
  totalPaid: z.number(),
  closingBalance: z.number(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const DebtReconciliationCreateInputSchema = /* @__PURE__ */ DebtReconciliationSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ openingBalance: true, totalIncurred: true, totalPaid: true, closingBalance: true, status: true });
export type DebtReconciliationRow = z.infer<typeof DebtReconciliationSchema>;
export type DebtReconciliationCreateInput = z.infer<typeof DebtReconciliationCreateInputSchema>;

export const ApprovalLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  entityType: z.string(),
  entityId: z.string(),
  action: z.string(),
  userId: z.string(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export const ApprovalLogCreateInputSchema = /* @__PURE__ */ ApprovalLogSchema.omit({ id: true, createdAt: true }).partial({ notes: true });
export type ApprovalLogRow = z.infer<typeof ApprovalLogSchema>;
export type ApprovalLogCreateInput = z.infer<typeof ApprovalLogCreateInputSchema>;

export const ProductionTaskSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  processType: z.string(),
  machineId: z.string().nullable(),
  priority: z.number().int(),
  plannedStartDate: z.coerce.date().nullable(),
  plannedEndDate: z.coerce.date().nullable(),
  layoutUps: z.number().int(),
  status: z.string(),
  plannedQty: z.number().int(),
  specification: z.string().nullable(),
  standardQuantity: z.number().int().nullable(),
  allowance: z.number().int().nullable(),
  materialRef: z.string().nullable(),
  plateRef: z.string().nullable(),
  outsourceRef: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProductionTaskCreateInputSchema = /* @__PURE__ */ ProductionTaskSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ machineId: true, priority: true, plannedStartDate: true, plannedEndDate: true, layoutUps: true, status: true, specification: true, standardQuantity: true, allowance: true, materialRef: true, plateRef: true, outsourceRef: true });
export type ProductionTaskRow = z.infer<typeof ProductionTaskSchema>;
export type ProductionTaskCreateInput = z.infer<typeof ProductionTaskCreateInputSchema>;

export const ProductionSessionSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  taskId: z.string(),
  machineId: z.string(),
  operatorId: z.string(),
  startTime: z.coerce.date(),
  endTime: z.coerce.date().nullable(),
  qtyOk: z.number().int(),
  qtyNg: z.number().int(),
  defectReason: z.string().nullable(),
  notes: z.string().nullable(),
  qcFirstPieceApproved: z.boolean(),
  qcInProcessNotes: z.string().nullable(),
  qcFinalVerified: z.boolean(),
  qcVerifierId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ProductionSessionCreateInputSchema = /* @__PURE__ */ ProductionSessionSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ startTime: true, endTime: true, qtyOk: true, qtyNg: true, defectReason: true, notes: true, qcFirstPieceApproved: true, qcInProcessNotes: true, qcFinalVerified: true, qcVerifierId: true });
export type ProductionSessionRow = z.infer<typeof ProductionSessionSchema>;
export type ProductionSessionCreateInput = z.infer<typeof ProductionSessionCreateInputSchema>;

export const MachineStateSchema = /* @__PURE__ */ z.object({
  machineId: z.string(),
  status: z.string(),
  currentTaskId: z.string().nullable(),
  operatorId: z.string().nullable(),
  lastHeartbeat: z.coerce.date(),
});
export const MachineStateCreateInputSchema = /* @__PURE__ */ MachineStateSchema.partial({ currentTaskId: true, operatorId: true, lastHeartbeat: true });
export type MachineStateRow = z.infer<typeof MachineStateSchema>;
export type MachineStateCreateInput = z.infer<typeof MachineStateCreateInputSchema>;

export const IoTDeviceSchema = /* @__PURE__ */ z.object({
  deviceId: z.string(),
  machineId: z.string(),
  macAddress: z.string().nullable(),
  ipAddress: z.string().nullable(),
  status: z.string(),
  lastPingAt: z.coerce.date(),
});
export const IoTDeviceCreateInputSchema = /* @__PURE__ */ IoTDeviceSchema.partial({ macAddress: true, ipAddress: true, status: true, lastPingAt: true });
export type IoTDeviceRow = z.infer<typeof IoTDeviceSchema>;
export type IoTDeviceCreateInput = z.infer<typeof IoTDeviceCreateInputSchema>;

export const MachineTelemetrySchema = /* @__PURE__ */ z.object({
  id: z.string(),
  machineId: z.string(),
  sessionId: z.string().nullable(),
  counterOk: z.number().int(),
  counterNg: z.number().int(),
  speed: z.number().nullable(),
  temperature: z.number().nullable(),
  timestamp: z.coerce.date(),
  createdAt: z.coerce.date(),
});
export const MachineTelemetryCreateInputSchema = /* @__PURE__ */ MachineTelemetrySchema.omit({ id: true, createdAt: true }).partial({ sessionId: true, counterOk: true, counterNg: true, speed: true, temperature: true });
export type MachineTelemetryRow = z.infer<typeof MachineTelemetrySchema>;
export type MachineTelemetryCreateInput = z.infer<typeof MachineTelemetryCreateInputSchema>;

export const PackingLabelSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  productionOrderId: z.string(),
  orderDetailId: z.string().nullable(),
  quantity: z.number(),
  cartonIndex: z.number().int(),
  printedAt: z.coerce.date().nullable(),
  deliveryId: z.string().nullable(),
  isDispatched: z.boolean(),
  dispatchedAt: z.coerce.date().nullable(),
  dispatchedBy: z.string().nullable(),
  packingSlipId: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export const PackingLabelCreateInputSchema = /* @__PURE__ */ PackingLabelSchema.omit({ id: true, createdAt: true }).partial({ orderDetailId: true, printedAt: true, deliveryId: true, isDispatched: true, dispatchedAt: true, dispatchedBy: true, packingSlipId: true });
export type PackingLabelRow = z.infer<typeof PackingLabelSchema>;
export type PackingLabelCreateInput = z.infer<typeof PackingLabelCreateInputSchema>;

export const ToolingLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  toolType: z.string(),
  status: ReadinessStatusEnumSchema,
  operatorId: z.string().nullable(),
  timestamp: z.coerce.date(),
  note: z.string().nullable(),
  localPath: z.string().nullable(),
  attachment: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const ToolingLogCreateInputSchema = /* @__PURE__ */ ToolingLogSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ status: true, operatorId: true, timestamp: true, note: true, localPath: true, attachment: true });
export type ToolingLogRow = z.infer<typeof ToolingLogSchema>;
export type ToolingLogCreateInput = z.infer<typeof ToolingLogCreateInputSchema>;

export const MaterialSupplyLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  materialId: z.string().nullable(),
  quantity: z.number(),
  status: ReadinessStatusEnumSchema,
  operatorId: z.string().nullable(),
  timestamp: z.coerce.date(),
  note: z.string().nullable(),
  lotNumber: z.string().nullable(),
  warehouseId: z.string().nullable(),
  docId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  issueType: z.string(),
  defectProcessId: z.string().nullable(),
  defectReason: z.string().nullable(),
  approvedBy: z.string().nullable(),
  approvalStatus: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const MaterialSupplyLogCreateInputSchema = /* @__PURE__ */ MaterialSupplyLogSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ materialId: true, quantity: true, status: true, operatorId: true, timestamp: true, note: true, lotNumber: true, warehouseId: true, docId: true, routingStepId: true, issueType: true, defectProcessId: true, defectReason: true, approvedBy: true, approvalStatus: true });
export type MaterialSupplyLogRow = z.infer<typeof MaterialSupplyLogSchema>;
export type MaterialSupplyLogCreateInput = z.infer<typeof MaterialSupplyLogCreateInputSchema>;

export const InventorySnapshotSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  checkId: z.string(),
  itemType: z.string(),
  itemId: z.string(),
  openingQty: z.number(),
  inQty: z.number(),
  outQty: z.number(),
  closingQty: z.number(),
  createdAt: z.coerce.date(),
});
export const InventorySnapshotCreateInputSchema = /* @__PURE__ */ InventorySnapshotSchema.omit({ id: true, createdAt: true }).partial({ openingQty: true, inQty: true, outQty: true, closingQty: true });
export type InventorySnapshotRow = z.infer<typeof InventorySnapshotSchema>;
export type InventorySnapshotCreateInput = z.infer<typeof InventorySnapshotCreateInputSchema>;

export const InventoryCheckSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  warehouseId: z.string().nullable(),
  checkDate: z.coerce.date(),
  status: z.string(),
  type: z.string(),
  notes: z.string().nullable(),
  receiptDocId: z.string().nullable(),
  receiptDocNo: z.string().nullable(),
  issueDocId: z.string().nullable(),
  issueDocNo: z.string().nullable(),
  completedAt: z.coerce.date().nullable(),
  completedBy: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const InventoryCheckCreateInputSchema = /* @__PURE__ */ InventoryCheckSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true }).partial({ warehouseId: true, status: true, notes: true, receiptDocId: true, receiptDocNo: true, issueDocId: true, issueDocNo: true, completedAt: true, completedBy: true });
export type InventoryCheckRow = z.infer<typeof InventoryCheckSchema>;
export type InventoryCheckCreateInput = z.infer<typeof InventoryCheckCreateInputSchema>;

export const InventoryCheckLineSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  checkId: z.string(),
  itemType: z.string(),
  itemId: z.string(),
  itemCode: z.string().nullable(),
  itemName: z.string().nullable(),
  unit: z.string().nullable(),
  lotNumber: z.string().nullable(),
  locationId: z.string().nullable(),
  systemQty: z.number(),
  actualQty: z.number(),
  diffQty: z.number(),
  reason: z.string().nullable(),
});
export const InventoryCheckLineCreateInputSchema = /* @__PURE__ */ InventoryCheckLineSchema.omit({ id: true }).partial({ itemCode: true, itemName: true, unit: true, lotNumber: true, locationId: true, reason: true });
export type InventoryCheckLineRow = z.infer<typeof InventoryCheckLineSchema>;
export type InventoryCheckLineCreateInput = z.infer<typeof InventoryCheckLineCreateInputSchema>;

export const EventErrorLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  eventName: z.string(),
  payload: jsonValueSchema.nullable(),
  errorMessage: z.string(),
  errorStack: z.string().nullable(),
  status: z.string(),
  retryCount: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const EventErrorLogCreateInputSchema = /* @__PURE__ */ EventErrorLogSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ payload: true, errorStack: true, status: true, retryCount: true });
export type EventErrorLogRow = z.infer<typeof EventErrorLogSchema>;
export type EventErrorLogCreateInput = z.infer<typeof EventErrorLogCreateInputSchema>;

export const PricingConfigMachineSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  maxSheetWidth: z.number(),
  maxSheetLength: z.number(),
  baseBatchSize: z.number().int(),
  cmykBatchCost: z.number(),
  cmykRunOnCost: z.number(),
  spotColorCostMultiplier: z.number(),
  uvInkCostMultiplier: z.number(),
  plateCost: z.number(),
  makeReadyCost: z.number(),
  washUpCostSpotColor: z.number(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const PricingConfigMachineCreateInputSchema = /* @__PURE__ */ PricingConfigMachineSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ spotColorCostMultiplier: true, uvInkCostMultiplier: true, plateCost: true, makeReadyCost: true, washUpCostSpotColor: true, status: true });
export type PricingConfigMachineRow = z.infer<typeof PricingConfigMachineSchema>;
export type PricingConfigMachineCreateInput = z.infer<typeof PricingConfigMachineCreateInputSchema>;

export const PricingConfigProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  type: z.string(),
  minBatchCost: z.number(),
  unitCost: z.number(),
  unitType: z.string(),
  moldPricingType: z.string().nullable(),
  baseMoldPrice: z.number().nullable(),
  moldFormula: z.string().nullable(),
  moldFixedCost: z.number(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const PricingConfigProcessCreateInputSchema = /* @__PURE__ */ PricingConfigProcessSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ moldPricingType: true, baseMoldPrice: true, moldFormula: true, moldFixedCost: true, status: true });
export type PricingConfigProcessRow = z.infer<typeof PricingConfigProcessSchema>;
export type PricingConfigProcessCreateInput = z.infer<typeof PricingConfigProcessCreateInputSchema>;

export const PricingConfigWastageSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  processType: z.string(),
  minQty: z.number().int(),
  maxQty: z.number().int(),
  setupWasteSheets: z.number().int(),
  runWastePercent: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const PricingConfigWastageCreateInputSchema = /* @__PURE__ */ PricingConfigWastageSchema.omit({ id: true, createdAt: true, updatedAt: true });
export type PricingConfigWastageRow = z.infer<typeof PricingConfigWastageSchema>;
export type PricingConfigWastageCreateInput = z.infer<typeof PricingConfigWastageCreateInputSchema>;

export const CostEstimateSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  estimateCode: z.string(),
  version: z.number().int(),
  totalMaterialCost: z.number(),
  totalProcessCost: z.number(),
  totalFixedCost: z.number(),
  marginPercent: z.number(),
  overheadPercent: z.number(),
  suggestedPrice: z.number(),
  financialParams: jsonValueSchema.nullable(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const CostEstimateCreateInputSchema = /* @__PURE__ */ CostEstimateSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ version: true, totalMaterialCost: true, totalProcessCost: true, totalFixedCost: true, marginPercent: true, overheadPercent: true, suggestedPrice: true, financialParams: true, status: true });
export type CostEstimateRow = z.infer<typeof CostEstimateSchema>;
export type CostEstimateCreateInput = z.infer<typeof CostEstimateCreateInputSchema>;

export const CostEstimateComponentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  estimateId: z.string(),
  name: z.string(),
  quantity: z.number().int(),
  flatWidth: z.number().nullable(),
  flatLength: z.number().nullable(),
  outUp: z.number().int(),
  sheetWidth: z.number().nullable(),
  sheetLength: z.number().nullable(),
  prepressParams: jsonValueSchema.nullable(),
  printColors: z.string().nullable(),
  printTech: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const CostEstimateComponentCreateInputSchema = /* @__PURE__ */ CostEstimateComponentSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ flatWidth: true, flatLength: true, outUp: true, sheetWidth: true, sheetLength: true, prepressParams: true, printColors: true, printTech: true });
export type CostEstimateComponentRow = z.infer<typeof CostEstimateComponentSchema>;
export type CostEstimateComponentCreateInput = z.infer<typeof CostEstimateComponentCreateInputSchema>;

export const CostEstimateMaterialSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  componentId: z.string(),
  materialId: z.string().nullable(),
  materialName: z.string(),
  printQty: z.number().int(),
  setupWasteQty: z.number().int(),
  runWasteQty: z.number().int(),
  totalSheets: z.number().int(),
  unitPrice: z.number(),
  totalCost: z.number(),
});
export const CostEstimateMaterialCreateInputSchema = /* @__PURE__ */ CostEstimateMaterialSchema.omit({ id: true }).partial({ materialId: true, setupWasteQty: true, runWasteQty: true });
export type CostEstimateMaterialRow = z.infer<typeof CostEstimateMaterialSchema>;
export type CostEstimateMaterialCreateInput = z.infer<typeof CostEstimateMaterialCreateInputSchema>;

export const CostEstimateRoutingSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  componentId: z.string(),
  processName: z.string(),
  processType: z.string(),
  isFixedCost: z.boolean(),
  moq: z.number().int().nullable(),
  batchCost: z.number().nullable(),
  minBatchCost: z.number().nullable(),
  unitCost: z.number().nullable(),
  calculatedCost: z.number(),
  machineId: z.string().nullable(),
  processId: z.string().nullable(),
});
export const CostEstimateRoutingCreateInputSchema = /* @__PURE__ */ CostEstimateRoutingSchema.omit({ id: true }).partial({ isFixedCost: true, moq: true, batchCost: true, minBatchCost: true, unitCost: true, machineId: true, processId: true });
export type CostEstimateRoutingRow = z.infer<typeof CostEstimateRoutingSchema>;
export type CostEstimateRoutingCreateInput = z.infer<typeof CostEstimateRoutingCreateInputSchema>;

export const PrintJobSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  jobCode: z.string(),
  name: z.string(),
  machineCode: z.string().nullable(),
  sheetWidth: z.number().nullable(),
  sheetLength: z.number().nullable(),
  totalPrintQty: z.number().int(),
  status: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const PrintJobCreateInputSchema = /* @__PURE__ */ PrintJobSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ machineCode: true, sheetWidth: true, sheetLength: true, status: true });
export type PrintJobRow = z.infer<typeof PrintJobSchema>;
export type PrintJobCreateInput = z.infer<typeof PrintJobCreateInputSchema>;

export const PrintLayoutSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string().nullable(),
  name: z.string(),
  status: PrepressStatusEnumSchema,
  gsm: z.number().int().nullable(),
  fileUrl: z.string().nullable(),
  layoutId: z.string().nullable(),
  layoutCode: z.string().nullable(),
  version: z.string().nullable(),
  dieCutId: z.string().nullable(),
  sheetsPerUp: z.number().int().nullable(),
  createdById: z.string().nullable(),
  sheetSize: z.string().nullable(),
  finishedSize: z.string().nullable(),
  prepressType: z.string().nullable(),
  ups: z.number().int().nullable(),
  gripperMargin: z.number().nullable(),
  bleed: z.number().nullable(),
  plateCount: z.number().int().nullable(),
  machineId: z.string().nullable(),
  notes: z.string().nullable(),
  componentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PrintLayoutCreateInputSchema = /* @__PURE__ */ PrintLayoutSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productionOrderId: true, status: true, gsm: true, fileUrl: true, layoutId: true, layoutCode: true, version: true, dieCutId: true, sheetsPerUp: true, createdById: true, sheetSize: true, finishedSize: true, prepressType: true, ups: true, gripperMargin: true, bleed: true, plateCount: true, machineId: true, notes: true, componentId: true });
export type PrintLayoutRow = z.infer<typeof PrintLayoutSchema>;
export type PrintLayoutCreateInput = z.infer<typeof PrintLayoutCreateInputSchema>;

export const PrintLayoutItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  printLayoutId: z.string(),
  orderDetailId: z.string(),
  ups: z.number().int(),
  sheetsPerUp: z.number().int(),
  plannedQty: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const PrintLayoutItemCreateInputSchema = /* @__PURE__ */ PrintLayoutItemSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ ups: true, sheetsPerUp: true, plannedQty: true, notes: true });
export type PrintLayoutItemRow = z.infer<typeof PrintLayoutItemSchema>;
export type PrintLayoutItemCreateInput = z.infer<typeof PrintLayoutItemCreateInputSchema>;

export const MaterialPlanSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productionOrderId: z.string(),
  componentId: z.string().nullable(),
  printLayoutId: z.string().nullable(),
  routingStepId: z.string().nullable(),
  originalMaterialId: z.string(),
  plannedMaterialId: z.string(),
  isPrint: z.boolean(),
  plannedQuantity: z.number(),
  unit: z.string(),
  qtyRequired: z.number(),
  qtyAvailable: z.number(),
  qtyToOrder: z.number(),
  setupSheets: z.number().int(),
  wastePercentage: z.number(),
  status: MaterialPlanningStatusEnumSchema,
  suppliedSize: z.string().nullable(),
  splitReason: z.string().nullable(),
  parentId: z.string().nullable(),
  expectedDate: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MaterialPlanCreateInputSchema = /* @__PURE__ */ MaterialPlanSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ componentId: true, printLayoutId: true, routingStepId: true, isPrint: true, plannedQuantity: true, unit: true, qtyRequired: true, qtyAvailable: true, qtyToOrder: true, setupSheets: true, wastePercentage: true, status: true, suppliedSize: true, splitReason: true, parentId: true, expectedDate: true });
export type MaterialPlanRow = z.infer<typeof MaterialPlanSchema>;
export type MaterialPlanCreateInput = z.infer<typeof MaterialPlanCreateInputSchema>;

export const FormulaTemplateSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  expression: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const FormulaTemplateCreateInputSchema = /* @__PURE__ */ FormulaTemplateSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ description: true });
export type FormulaTemplateRow = z.infer<typeof FormulaTemplateSchema>;
export type FormulaTemplateCreateInput = z.infer<typeof FormulaTemplateCreateInputSchema>;

export const FormulaVariableSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  formulaTemplateId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  defaultValue: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const FormulaVariableCreateInputSchema = /* @__PURE__ */ FormulaVariableSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial({ description: true, defaultValue: true });
export type FormulaVariableRow = z.infer<typeof FormulaVariableSchema>;
export type FormulaVariableCreateInput = z.infer<typeof FormulaVariableCreateInputSchema>;

export const CustomerDebtSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  customerId: z.string(),
  amount: z.number(),
  type: DebtTypeEnumSchema,
  referenceId: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CustomerDebtCreateInputSchema = /* @__PURE__ */ CustomerDebtSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ type: true, referenceId: true, description: true });
export type CustomerDebtRow = z.infer<typeof CustomerDebtSchema>;
export type CustomerDebtCreateInput = z.infer<typeof CustomerDebtCreateInputSchema>;

export const CustomerPaymentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  customerId: z.string(),
  amount: z.number(),
  paymentDate: z.coerce.date(),
  method: z.string().nullable(),
  referenceId: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const CustomerPaymentCreateInputSchema = /* @__PURE__ */ CustomerPaymentSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ method: true, referenceId: true, description: true });
export type CustomerPaymentRow = z.infer<typeof CustomerPaymentSchema>;
export type CustomerPaymentCreateInput = z.infer<typeof CustomerPaymentCreateInputSchema>;

export const VendorDebtSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  vendorId: z.string(),
  amount: z.number(),
  type: DebtTypeEnumSchema,
  referenceId: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const VendorDebtCreateInputSchema = /* @__PURE__ */ VendorDebtSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ type: true, referenceId: true, description: true });
export type VendorDebtRow = z.infer<typeof VendorDebtSchema>;
export type VendorDebtCreateInput = z.infer<typeof VendorDebtCreateInputSchema>;

export const VendorPaymentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  vendorId: z.string(),
  amount: z.number(),
  paymentDate: z.coerce.date(),
  method: z.string().nullable(),
  referenceId: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const VendorPaymentCreateInputSchema = /* @__PURE__ */ VendorPaymentSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ method: true, referenceId: true, description: true });
export type VendorPaymentRow = z.infer<typeof VendorPaymentSchema>;
export type VendorPaymentCreateInput = z.infer<typeof VendorPaymentCreateInputSchema>;

export const VendorDebtReconciliationSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  vendorId: z.string(),
  month: z.number().int(),
  year: z.number().int(),
  openingBalance: z.number(),
  totalIncurred: z.number(),
  totalPaid: z.number(),
  closingBalance: z.number(),
  status: z.string(),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export const VendorDebtReconciliationCreateInputSchema = /* @__PURE__ */ VendorDebtReconciliationSchema.omit({ id: true, createdBy: true, createdAt: true, updatedAt: true }).partial({ openingBalance: true, totalIncurred: true, totalPaid: true, closingBalance: true, status: true });
export type VendorDebtReconciliationRow = z.infer<typeof VendorDebtReconciliationSchema>;
export type VendorDebtReconciliationCreateInput = z.infer<typeof VendorDebtReconciliationCreateInputSchema>;

export const MachineSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  machineId: z.string(),
  name: z.string(),
  type: MachineTypeEnumSchema,
  processCategory: z.string().nullable(),
  imageUrl: z.string().nullable(),
  manufacturer: z.string().nullable(),
  countryOfOrigin: z.string().nullable(),
  yearManufactured: z.string().nullable(),
  serialNumber: z.string().nullable(),
  workingFormat: z.string().nullable(),
  plateSize: z.string().nullable(),
  paperWeightRange: z.string().nullable(),
  powerConsumptionKw: z.number().nullable(),
  technicalModel: z.string().nullable(),
  technicalSpecs: jsonValueSchema.nullable(),
  standardProductivity: z.number().int().nullable(),
  setupTimeMinutes: z.number().int().nullable(),
  hourlyRate: z.number().nullable(),
  costPerHour: z.number().nullable(),
  status: MachineStatusEnumSchema,
  totalRunningHours: z.number().nullable(),
  totalOutputQuantity: z.number().int().nullable(),
  maintenanceCycleHours: z.number().int().nullable(),
  maintenanceCycleDays: z.number().int().nullable(),
  nextMaintenanceDate: z.coerce.date().nullable(),
  nextMaintenanceHours: z.number().nullable(),
  description: z.string().nullable(),
  specs: jsonValueSchema.nullable(),
  legacyModel: z.string().nullable(),
  legacyMaintenanceCycleMonths: z.number().int().nullable(),
  legacyQuantity: z.number().int().nullable(),
  legacyIsDeleted: z.boolean().nullable(),
  departmentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  machineStateMachineId: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const MachineCreateInputSchema = /* @__PURE__ */ MachineSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ processCategory: true, imageUrl: true, manufacturer: true, countryOfOrigin: true, yearManufactured: true, serialNumber: true, workingFormat: true, plateSize: true, paperWeightRange: true, powerConsumptionKw: true, technicalModel: true, technicalSpecs: true, standardProductivity: true, setupTimeMinutes: true, hourlyRate: true, costPerHour: true, status: true, totalRunningHours: true, totalOutputQuantity: true, maintenanceCycleHours: true, maintenanceCycleDays: true, nextMaintenanceDate: true, nextMaintenanceHours: true, description: true, specs: true, legacyModel: true, legacyMaintenanceCycleMonths: true, legacyQuantity: true, legacyIsDeleted: true, departmentId: true, machineStateMachineId: true });
export type MachineRow = z.infer<typeof MachineSchema>;
export type MachineCreateInput = z.infer<typeof MachineCreateInputSchema>;

export const IqcRecordSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  iqcCode: z.string(),
  purchaseOrderId: z.string(),
  vendorId: z.string(),
  status: z.string(),
  inspectorId: z.string(),
  inspectedAt: z.coerce.date().nullable(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const IqcRecordCreateInputSchema = /* @__PURE__ */ IqcRecordSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ status: true, inspectedAt: true, notes: true });
export type IqcRecordRow = z.infer<typeof IqcRecordSchema>;
export type IqcRecordCreateInput = z.infer<typeof IqcRecordCreateInputSchema>;

export const IqcRecordItemSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  iqcRecordId: z.string(),
  materialId: z.string(),
  receivedQty: z.number(),
  passedQty: z.number(),
  failedQty: z.number(),
  defectReason: z.string().nullable(),
});
export const IqcRecordItemCreateInputSchema = /* @__PURE__ */ IqcRecordItemSchema.omit({ id: true }).partial({ passedQty: true, failedQty: true, defectReason: true });
export type IqcRecordItemRow = z.infer<typeof IqcRecordItemSchema>;
export type IqcRecordItemCreateInput = z.infer<typeof IqcRecordItemCreateInputSchema>;

export const ProductSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productCode: z.string(),
  productName: z.string(),
  customerId: z.string().nullable(),
  typeId: z.string().nullable(),
  materialId: z.string().nullable(),
  active: z.boolean(),
  specs: jsonValueSchema.nullable(),
  length: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  flatLength: z.number().nullable(),
  flatWidth: z.number().nullable(),
  printFormat: z.string().nullable(),
  cutFormat: z.string().nullable(),
  outs: z.number().int().nullable(),
  currentVersion: z.number().int(),
  standardCost: z.number().nullable(),
  packingSpec: z.string().nullable(),
  notes: z.string().nullable(),
  pcsPerBox: z.number().int().nullable(),
  unit: z.string().nullable(),
  price: z.number().nullable(),
  costPrice: z.number().nullable(),
  process: z.string().nullable(),
  image: z.string().nullable(),
  tags: z.string().nullable(),
  customerCode: z.string().nullable(),
  lastPrice: z.number().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isComposite: z.boolean(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductCreateInputSchema = /* @__PURE__ */ ProductSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ customerId: true, typeId: true, materialId: true, active: true, specs: true, length: true, width: true, height: true, flatLength: true, flatWidth: true, printFormat: true, cutFormat: true, outs: true, currentVersion: true, standardCost: true, packingSpec: true, notes: true, pcsPerBox: true, unit: true, price: true, costPrice: true, process: true, image: true, tags: true, customerCode: true, lastPrice: true, isComposite: true });
export type ProductRow = z.infer<typeof ProductSchema>;
export type ProductCreateInput = z.infer<typeof ProductCreateInputSchema>;

export const ProductTypeSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  code: z.string(),
  category: z.string().nullable(),
  group: z.string().nullable(),
  unit: z.string().nullable(),
  standardProcess: z.string().nullable(),
  productionDays: z.number().int().nullable(),
  profitMargin: z.string().nullable(),
  image: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductTypeCreateInputSchema = /* @__PURE__ */ ProductTypeSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ category: true, group: true, unit: true, standardProcess: true, productionDays: true, profitMargin: true, image: true });
export type ProductTypeRow = z.infer<typeof ProductTypeSchema>;
export type ProductTypeCreateInput = z.infer<typeof ProductTypeCreateInputSchema>;

export const ProductProcessSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productId: z.string(),
  componentId: z.string().nullable(),
  processId: z.string(),
  sortOrder: z.number().int(),
  note: z.string().nullable(),
  setupTimeMinutes: z.number().int().nullable(),
  speedPerHour: z.number().int().nullable(),
  setupWasteQty: z.number().int().nullable(),
  runWastePercent: z.number().nullable(),
  isOutsourced: z.boolean(),
  priceUnit: PriceUnitEnumSchema.nullable(),
  moq: z.number().int().nullable(),
  batchCost: z.number().nullable(),
  unitCost: z.number().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductProcessCreateInputSchema = /* @__PURE__ */ ProductProcessSchema.omit({ id: true, isDeleted: true, deletedAt: true }).partial({ componentId: true, sortOrder: true, note: true, setupTimeMinutes: true, speedPerHour: true, setupWasteQty: true, runWastePercent: true, isOutsourced: true, priceUnit: true, moq: true, batchCost: true, unitCost: true });
export type ProductProcessRow = z.infer<typeof ProductProcessSchema>;
export type ProductProcessCreateInput = z.infer<typeof ProductProcessCreateInputSchema>;

export const ProductMaterialSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productId: z.string(),
  componentId: z.string().nullable(),
  materialId: z.string(),
  productProcessId: z.string().nullable(),
  standardQuantity: z.number().nullable(),
  sortOrder: z.number().int(),
  note: z.string().nullable(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductMaterialCreateInputSchema = /* @__PURE__ */ ProductMaterialSchema.omit({ id: true, isDeleted: true, deletedAt: true }).partial({ componentId: true, productProcessId: true, standardQuantity: true, sortOrder: true, note: true });
export type ProductMaterialRow = z.infer<typeof ProductMaterialSchema>;
export type ProductMaterialCreateInput = z.infer<typeof ProductMaterialCreateInputSchema>;

export const ProductComponentSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productId: z.string(),
  componentName: z.string(),
  quantity: z.number().int(),
  unit: z.string().nullable(),
  length: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  flatLength: z.number().nullable(),
  flatWidth: z.number().nullable(),
  printFormat: z.string().nullable(),
  cutFormat: z.string().nullable(),
  materialId: z.string().nullable(),
  process: z.string().nullable(),
  specs: z.string().nullable(),
  frontColors: z.number().int(),
  backColors: z.number().int(),
  spotColors: z.number().int(),
  hasUV: z.boolean(),
  outUp: z.number().int(),
  prepressType: z.string().nullable(),
  sortOrder: z.number().int(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const ProductComponentCreateInputSchema = /* @__PURE__ */ ProductComponentSchema.omit({ id: true, isDeleted: true, deletedAt: true }).partial({ quantity: true, unit: true, length: true, width: true, height: true, flatLength: true, flatWidth: true, printFormat: true, cutFormat: true, materialId: true, process: true, specs: true, frontColors: true, backColors: true, spotColors: true, hasUV: true, outUp: true, prepressType: true, sortOrder: true });
export type ProductComponentRow = z.infer<typeof ProductComponentSchema>;
export type ProductComponentCreateInput = z.infer<typeof ProductComponentCreateInputSchema>;

export const ProductVersionLogSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productId: z.string(),
  version: z.number().int(),
  snapshot: jsonValueSchema,
  createdAt: z.coerce.date(),
});
export const ProductVersionLogCreateInputSchema = /* @__PURE__ */ ProductVersionLogSchema.omit({ id: true, createdAt: true });
export type ProductVersionLogRow = z.infer<typeof ProductVersionLogSchema>;
export type ProductVersionLogCreateInput = z.infer<typeof ProductVersionLogCreateInputSchema>;

export const DieCutProductSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  dieCutId: z.string(),
  productId: z.string(),
  ups: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export const DieCutProductCreateInputSchema = /* @__PURE__ */ DieCutProductSchema.omit({ id: true, createdAt: true }).partial({ ups: true, notes: true });
export type DieCutProductRow = z.infer<typeof DieCutProductSchema>;
export type DieCutProductCreateInput = z.infer<typeof DieCutProductCreateInputSchema>;

export const DesignRequestSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  productId: z.string(),
  designType: DesignTypeEnumSchema,
  quantity: z.number().int().nullable(),
  images: jsonValueSchema.nullable(),
  filePaths: jsonValueSchema.nullable(),
  status: DesignStatusEnumSchema,
  requestedBy: z.string().nullable(),
  assignedTo: z.string().nullable(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const DesignRequestCreateInputSchema = /* @__PURE__ */ DesignRequestSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ designType: true, quantity: true, images: true, filePaths: true, status: true, requestedBy: true, assignedTo: true, description: true });
export type DesignRequestRow = z.infer<typeof DesignRequestSchema>;
export type DesignRequestCreateInput = z.infer<typeof DesignRequestCreateInputSchema>;

export const TechnicalSpecSchema = /* @__PURE__ */ z.object({
  id: z.string(),
  specId: z.string(),
  productId: z.string().nullable(),
  productName: z.string().nullable(),
  finishedSize: jsonValueSchema.nullable(),
  flatSize: z.string().nullable(),
  materialStructure: z.string().nullable(),
  printTech: z.string().nullable(),
  printColors: z.string().nullable(),
  finish: z.array(z.string()),
  layoutId: z.string().nullable(),
  dieCutId: z.string().nullable(),
  foilDieId: z.string().nullable(),
  embossDieId: z.string().nullable(),
  screenId: z.string().nullable(),
  designFile: z.string().nullable(),
  designStatus: DesignStatusEnumSchema.nullable(),
  designNotes: z.string().nullable(),
  notes: z.string().nullable(),
  documentNo: z.string().nullable(),
  version: z.string(),
  issueDate: z.coerce.date().nullable(),
  status: SpecStatusEnumSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isDeleted: z.boolean(),
  deletedAt: z.coerce.date().nullable(),
});
export const TechnicalSpecCreateInputSchema = /* @__PURE__ */ TechnicalSpecSchema.omit({ id: true, createdAt: true, updatedAt: true, isDeleted: true, deletedAt: true }).partial({ productId: true, productName: true, finishedSize: true, flatSize: true, materialStructure: true, printTech: true, printColors: true, finish: true, layoutId: true, dieCutId: true, foilDieId: true, embossDieId: true, screenId: true, designFile: true, designStatus: true, designNotes: true, notes: true, documentNo: true, version: true, issueDate: true, status: true });
export type TechnicalSpecRow = z.infer<typeof TechnicalSpecSchema>;
export type TechnicalSpecCreateInput = z.infer<typeof TechnicalSpecCreateInputSchema>;

// ── Registry (dùng cho script kiểm tra lệch giữa schema viết tay và DB) ──

export const prismaModelSchemas = {
  User: { row: UserSchema, create: UserCreateInputSchema },
  EmployeeContract: { row: EmployeeContractSchema, create: EmployeeContractCreateInputSchema },
  Session: { row: SessionSchema, create: SessionCreateInputSchema },
  Role: { row: RoleSchema, create: RoleCreateInputSchema },
  Department: { row: DepartmentSchema, create: DepartmentCreateInputSchema },
  SystemConfig: { row: SystemConfigSchema, create: SystemConfigCreateInputSchema },
  Notification: { row: NotificationSchema, create: NotificationCreateInputSchema },
  SystemChangelog: { row: SystemChangelogSchema, create: SystemChangelogCreateInputSchema },
  Sequence: { row: SequenceSchema, create: SequenceCreateInputSchema },
  TrashRecord: { row: TrashRecordSchema, create: TrashRecordCreateInputSchema },
  Customer: { row: CustomerSchema, create: CustomerCreateInputSchema },
  CustomerSalesUser: { row: CustomerSalesUserSchema, create: CustomerSalesUserCreateInputSchema },
  Material: { row: MaterialSchema, create: MaterialCreateInputSchema },
  MaterialType: { row: MaterialTypeSchema, create: MaterialTypeCreateInputSchema },
  Quotation: { row: QuotationSchema, create: QuotationCreateInputSchema },
  QuotationDetail: { row: QuotationDetailSchema, create: QuotationDetailCreateInputSchema },
  QuotationDetailComponent: { row: QuotationDetailComponentSchema, create: QuotationDetailComponentCreateInputSchema },
  Order: { row: OrderSchema, create: OrderCreateInputSchema },
  OrderDetail: { row: OrderDetailSchema, create: OrderDetailCreateInputSchema },
  OrderDetailComponent: { row: OrderDetailComponentSchema, create: OrderDetailComponentCreateInputSchema },
  ProductionOrder: { row: ProductionOrderSchema, create: ProductionOrderCreateInputSchema },
  ProductionOrderSource: { row: ProductionOrderSourceSchema, create: ProductionOrderSourceCreateInputSchema },
  ProductionOrderPrepress: { row: ProductionOrderPrepressSchema, create: ProductionOrderPrepressCreateInputSchema },
  ProductionOrderTooling: { row: ProductionOrderToolingSchema, create: ProductionOrderToolingCreateInputSchema },
  ProductionOrderMaterial: { row: ProductionOrderMaterialSchema, create: ProductionOrderMaterialCreateInputSchema },
  ProductionOrderComponent: { row: ProductionOrderComponentSchema, create: ProductionOrderComponentCreateInputSchema },
  RoutingStep: { row: RoutingStepSchema, create: RoutingStepCreateInputSchema },
  BOM: { row: BOMSchema, create: BOMCreateInputSchema },
  Process: { row: ProcessSchema, create: ProcessCreateInputSchema },
  ProcessGroup: { row: ProcessGroupSchema, create: ProcessGroupCreateInputSchema },
  ProcessWasteMatrix: { row: ProcessWasteMatrixSchema, create: ProcessWasteMatrixCreateInputSchema },
  UserProcess: { row: UserProcessSchema, create: UserProcessCreateInputSchema },
  MachineProcess: { row: MachineProcessSchema, create: MachineProcessCreateInputSchema },
  MachineLog: { row: MachineLogSchema, create: MachineLogCreateInputSchema },
  ProductionLog: { row: ProductionLogSchema, create: ProductionLogCreateInputSchema },
  WIPTransfer: { row: WIPTransferSchema, create: WIPTransferCreateInputSchema },
  QCTemplate: { row: QCTemplateSchema, create: QCTemplateCreateInputSchema },
  QCRecord: { row: QCRecordSchema, create: QCRecordCreateInputSchema },
  QCCheck: { row: QCCheckSchema, create: QCCheckCreateInputSchema },
  QCProcess: { row: QCProcessSchema, create: QCProcessCreateInputSchema },
  QCMaterial: { row: QCMaterialSchema, create: QCMaterialCreateInputSchema },
  QCFinal: { row: QCFinalSchema, create: QCFinalCreateInputSchema },
  PackingSlip: { row: PackingSlipSchema, create: PackingSlipCreateInputSchema },
  NCR: { row: NCRSchema, create: NCRCreateInputSchema },
  Vendor: { row: VendorSchema, create: VendorCreateInputSchema },
  OutsourcePO: { row: OutsourcePOSchema, create: OutsourcePOCreateInputSchema },
  Warehouse: { row: WarehouseSchema, create: WarehouseCreateInputSchema },
  WarehouseLocation: { row: WarehouseLocationSchema, create: WarehouseLocationCreateInputSchema },
  InventoryBalance: { row: InventoryBalanceSchema, create: InventoryBalanceCreateInputSchema },
  InventoryTransaction: { row: InventoryTransactionSchema, create: InventoryTransactionCreateInputSchema },
  InventoryDocument: { row: InventoryDocumentSchema, create: InventoryDocumentCreateInputSchema },
  InventoryDocumentLine: { row: InventoryDocumentLineSchema, create: InventoryDocumentLineCreateInputSchema },
  ToolingItem: { row: ToolingItemSchema, create: ToolingItemCreateInputSchema },
  PurchaseOrder: { row: PurchaseOrderSchema, create: PurchaseOrderCreateInputSchema },
  PurchaseOrderItem: { row: PurchaseOrderItemSchema, create: PurchaseOrderItemCreateInputSchema },
  PurchaseRequest: { row: PurchaseRequestSchema, create: PurchaseRequestCreateInputSchema },
  Delivery: { row: DeliverySchema, create: DeliveryCreateInputSchema },
  DeliveryItem: { row: DeliveryItemSchema, create: DeliveryItemCreateInputSchema },
  Plate: { row: PlateSchema, create: PlateCreateInputSchema },
  CtpPlateLog: { row: CtpPlateLogSchema, create: CtpPlateLogCreateInputSchema },
  PlateUsageHistory: { row: PlateUsageHistorySchema, create: PlateUsageHistoryCreateInputSchema },
  DieCut: { row: DieCutSchema, create: DieCutCreateInputSchema },
  DieCutUsageHistory: { row: DieCutUsageHistorySchema, create: DieCutUsageHistoryCreateInputSchema },
  DieCutLog: { row: DieCutLogSchema, create: DieCutLogCreateInputSchema },
  Layout: { row: LayoutSchema, create: LayoutCreateInputSchema },
  Changelog: { row: ChangelogSchema, create: ChangelogCreateInputSchema },
  UserGuide: { row: UserGuideSchema, create: UserGuideCreateInputSchema },
  Comment: { row: CommentSchema, create: CommentCreateInputSchema },
  ActivityLog: { row: ActivityLogSchema, create: ActivityLogCreateInputSchema },
  Attachment: { row: AttachmentSchema, create: AttachmentCreateInputSchema },
  BackgroundJob: { row: BackgroundJobSchema, create: BackgroundJobCreateInputSchema },
  ConfigCategory: { row: ConfigCategorySchema, create: ConfigCategoryCreateInputSchema },
  ConfigItem: { row: ConfigItemSchema, create: ConfigItemCreateInputSchema },
  WorkflowTransition: { row: WorkflowTransitionSchema, create: WorkflowTransitionCreateInputSchema },
  Feedback: { row: FeedbackSchema, create: FeedbackCreateInputSchema },
  UserEvent: { row: UserEventSchema, create: UserEventCreateInputSchema },
  FeedbackComment: { row: FeedbackCommentSchema, create: FeedbackCommentCreateInputSchema },
  Kpi: { row: KpiSchema, create: KpiCreateInputSchema },
  Receipt: { row: ReceiptSchema, create: ReceiptCreateInputSchema },
  DebtReconciliation: { row: DebtReconciliationSchema, create: DebtReconciliationCreateInputSchema },
  ApprovalLog: { row: ApprovalLogSchema, create: ApprovalLogCreateInputSchema },
  ProductionTask: { row: ProductionTaskSchema, create: ProductionTaskCreateInputSchema },
  ProductionSession: { row: ProductionSessionSchema, create: ProductionSessionCreateInputSchema },
  MachineState: { row: MachineStateSchema, create: MachineStateCreateInputSchema },
  IoTDevice: { row: IoTDeviceSchema, create: IoTDeviceCreateInputSchema },
  MachineTelemetry: { row: MachineTelemetrySchema, create: MachineTelemetryCreateInputSchema },
  PackingLabel: { row: PackingLabelSchema, create: PackingLabelCreateInputSchema },
  ToolingLog: { row: ToolingLogSchema, create: ToolingLogCreateInputSchema },
  MaterialSupplyLog: { row: MaterialSupplyLogSchema, create: MaterialSupplyLogCreateInputSchema },
  InventorySnapshot: { row: InventorySnapshotSchema, create: InventorySnapshotCreateInputSchema },
  InventoryCheck: { row: InventoryCheckSchema, create: InventoryCheckCreateInputSchema },
  InventoryCheckLine: { row: InventoryCheckLineSchema, create: InventoryCheckLineCreateInputSchema },
  EventErrorLog: { row: EventErrorLogSchema, create: EventErrorLogCreateInputSchema },
  PricingConfigMachine: { row: PricingConfigMachineSchema, create: PricingConfigMachineCreateInputSchema },
  PricingConfigProcess: { row: PricingConfigProcessSchema, create: PricingConfigProcessCreateInputSchema },
  PricingConfigWastage: { row: PricingConfigWastageSchema, create: PricingConfigWastageCreateInputSchema },
  CostEstimate: { row: CostEstimateSchema, create: CostEstimateCreateInputSchema },
  CostEstimateComponent: { row: CostEstimateComponentSchema, create: CostEstimateComponentCreateInputSchema },
  CostEstimateMaterial: { row: CostEstimateMaterialSchema, create: CostEstimateMaterialCreateInputSchema },
  CostEstimateRouting: { row: CostEstimateRoutingSchema, create: CostEstimateRoutingCreateInputSchema },
  PrintJob: { row: PrintJobSchema, create: PrintJobCreateInputSchema },
  PrintLayout: { row: PrintLayoutSchema, create: PrintLayoutCreateInputSchema },
  PrintLayoutItem: { row: PrintLayoutItemSchema, create: PrintLayoutItemCreateInputSchema },
  MaterialPlan: { row: MaterialPlanSchema, create: MaterialPlanCreateInputSchema },
  FormulaTemplate: { row: FormulaTemplateSchema, create: FormulaTemplateCreateInputSchema },
  FormulaVariable: { row: FormulaVariableSchema, create: FormulaVariableCreateInputSchema },
  CustomerDebt: { row: CustomerDebtSchema, create: CustomerDebtCreateInputSchema },
  CustomerPayment: { row: CustomerPaymentSchema, create: CustomerPaymentCreateInputSchema },
  VendorDebt: { row: VendorDebtSchema, create: VendorDebtCreateInputSchema },
  VendorPayment: { row: VendorPaymentSchema, create: VendorPaymentCreateInputSchema },
  VendorDebtReconciliation: { row: VendorDebtReconciliationSchema, create: VendorDebtReconciliationCreateInputSchema },
  Machine: { row: MachineSchema, create: MachineCreateInputSchema },
  IqcRecord: { row: IqcRecordSchema, create: IqcRecordCreateInputSchema },
  IqcRecordItem: { row: IqcRecordItemSchema, create: IqcRecordItemCreateInputSchema },
  Product: { row: ProductSchema, create: ProductCreateInputSchema },
  ProductType: { row: ProductTypeSchema, create: ProductTypeCreateInputSchema },
  ProductProcess: { row: ProductProcessSchema, create: ProductProcessCreateInputSchema },
  ProductMaterial: { row: ProductMaterialSchema, create: ProductMaterialCreateInputSchema },
  ProductComponent: { row: ProductComponentSchema, create: ProductComponentCreateInputSchema },
  ProductVersionLog: { row: ProductVersionLogSchema, create: ProductVersionLogCreateInputSchema },
  DieCutProduct: { row: DieCutProductSchema, create: DieCutProductCreateInputSchema },
  DesignRequest: { row: DesignRequestSchema, create: DesignRequestCreateInputSchema },
  TechnicalSpec: { row: TechnicalSpecSchema, create: TechnicalSpecCreateInputSchema },
} as const;

export type PrismaModelName = keyof typeof prismaModelSchemas;
